import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, apiFetch } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Settings, Users, CreditCard, Music, BarChart3,
  Save, Loader2, DollarSign, Zap, Image, Gift, Edit2, Edit, X, Plus, Minus, Tag, Trash2, AlertTriangle, MessageSquare, Cake,
  HeartPulse, CheckCircle, XCircle, RefreshCw, Search, ExternalLink, Download, FileText, Upload, Play, Pause, Sparkles, Database, Bug, ArrowUpDown, ArrowUp, ArrowDown, Film, Eye, TrendingUp
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import AdminStylePresetsTab from '../components/AdminStylePresetsTab';
import AdminAnimationStudioTab from '../components/AdminAnimationStudioTab';
import AdminCoachTab from '../components/AdminCoachTab';
import AdminDemoFunnelTab from '../components/AdminDemoFunnelTab';
import AudioMirrorCard from '../components/AudioMirrorCard';
import CoverMirrorCard from '../components/CoverMirrorCard';
import SongProfitabilityExport from '../components/SongProfitabilityExport';
import AdminDbMigrateTab from '../components/AdminDbMigrateTab';
import AdminPayPalStatus from '../components/AdminPayPalStatus';
import AdminAtlasCloudStatus from '../components/AdminAtlasCloudStatus';
import AdminMaintenanceModeCard from '../components/AdminMaintenanceModeCard';
import LiveUsersWidget from '../components/LiveUsersWidget';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_gift-melody-2/artifacts/vgbmb5gp_Logo-Wrapped%20Music1.png";
const BANNER_PLAIN = "/assets/brand/logo-inline.png";

// ---------------------------------------------------------------------------
// nativeFetchWithAuth — drop-in wrapper around the native `fetch` that ALWAYS
// forwards the bearer token from localStorage along with `credentials: include`.
//
// Why this exists: the preview environment doesn't set the session cookie
// that production relies on, so raw `fetch(url, {credentials: 'include'})`
// calls return 401 on every admin endpoint and bounce the admin to /login
// (the recurring "preview is broken" bug). The token-based path works in
// both envs, so we standardise on it for every admin request below.
//
// Identical API to native fetch (returns a Response object) so existing
// `.ok / .status / .json()` callsites need no change.
// ---------------------------------------------------------------------------
const nativeFetchWithAuth = (url, options = {}) => {
  let token = '';
  try { token = localStorage.getItem('wim_session_token') || ''; } catch { /* noop */ }
  const headers = { ...(options.headers || {}) };
  if (token && !headers.Authorization) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { credentials: 'include', ...options, headers });
};

const LogoNav = () => (
  <div className="flex items-center">
    <img src={BANNER_PLAIN} alt="Wrapped In Music" className="h-8 w-auto" />
  </div>
);

// Cancelled Renders Manager Component
const CancelledRendersManager = () => {
  const [cancelledSongs, setCancelledSongs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [checking, setChecking] = useState(null);

  const fetchCancelledRenders = async () => {
    setLoading(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/cancelled-renders`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCancelledSongs(data.cancelled_renders || []);
      }
    } catch (e) {
      console.error('Error fetching cancelled renders:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCancelledRenders();
  }, []);

  const handleRestore = async (songId) => {
    setRestoring(songId);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/restore-cancelled-song/${songId}`, { 
        method: 'POST', 
        credentials: 'include' 
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Song restored to original user!');
        fetchCancelledRenders();
      } else {
        toast.error(data.detail || 'Failed to restore');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
    setRestoring(null);
  };

  const handleDelete = async (songId) => {
    if (!confirm('Permanently delete this cancelled song? This cannot be undone.')) return;
    try {
      const res = await nativeFetchWithAuth(`${API}/songs/${songId}`, { 
        method: 'DELETE', 
        credentials: 'include',
        headers: { 'X-Admin-Delete': 'true' }
      });
      if (res.ok) {
        toast.success('Song deleted');
        fetchCancelledRenders();
      } else {
        toast.error('Failed to delete');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
  };

  const handleForceCheckSuno = async (songId) => {
    setChecking(songId);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/check-cancelled-song-suno/${songId}`, {
        method: 'POST',
        credentials: 'include'
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Force-check failed');
        setChecking(null);
        return;
      }
      const result = data.result;
      const msg = data.message || 'Done';
      if (result === 'recovered') {
        toast.success(msg, { duration: 6000 });
        fetchCancelledRenders();
      } else if (result === 'failed_on_suno' || result === 'no_task_id' || result === 'completed_no_audio' || result === 'completed_no_data') {
        toast.warning(msg, { duration: 6000 });
      } else if (result === 'still_processing') {
        toast.info(msg, { duration: 6000 });
      } else {
        toast.message(msg, { duration: 6000 });
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
    setChecking(null);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-zinc-400 text-sm">{cancelledSongs.length} cancelled render(s)</span>
        <Button
          size="sm"
          variant="outline"
          onClick={fetchCancelledRenders}
          disabled={loading}
          className="border-white/20"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {cancelledSongs.length === 0 ? (
        <div className="text-center py-8 text-zinc-500">
          No cancelled renders found
        </div>
      ) : (
        <div className="space-y-3">
          {cancelledSongs.map((song) => (
            <div key={song.song_id} className="p-4 bg-black/20 rounded-lg border border-white/10">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="text-white font-medium">{song.title || 'Untitled'}</h4>
                  <div className="text-xs text-zinc-400 mt-1 space-y-0.5">
                    <p>Original User: {song.original_user_email || song.original_user_id}</p>
                    <p>Cancelled: {song.cancelled_at ? new Date(song.cancelled_at).toLocaleString() : 'Unknown'}</p>
                    <p>Status: <span className={song.status === 'completed' ? 'text-green-400' : 'text-orange-400'}>{song.status}</span></p>
                    {song.audio_url && <p className="text-green-400">✓ Audio ready</p>}
                  </div>
                </div>
                <div className="flex gap-2">
                  {song.status === 'completed' && song.audio_url && (
                    <Button
                      size="sm"
                      onClick={() => handleRestore(song.song_id)}
                      disabled={restoring === song.song_id}
                      className="bg-green-600 hover:bg-green-700 text-white"
                      data-testid={`restore-cancelled-${song.song_id}`}
                    >
                      {restoring === song.song_id ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Restore'}
                    </Button>
                  )}
                  {song.status !== 'completed' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleForceCheckSuno(song.song_id)}
                      disabled={checking === song.song_id}
                      className="border-blue-500/40 text-blue-300 hover:bg-blue-500/10"
                      title="Force an on-demand SUNO check (bypasses 20-min timeout)"
                      data-testid={`force-check-suno-${song.song_id}`}
                    >
                      {checking === song.song_id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><RefreshCw className="h-4 w-4 mr-1" />Check SUNO</>
                      )}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(song.song_id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`delete-cancelled-${song.song_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Cancelled Renders Account Setting Component
const CancelledRendersAccountSetting = () => {
  const [currentAccount, setCurrentAccount] = useState(null);
  const [newEmail, setNewEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchCurrentAccount = async () => {
    setLoading(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/settings/cancelled-renders-user`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCurrentAccount(data);
      }
    } catch (e) {
      console.error('Error fetching cancelled renders account:', e);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchCurrentAccount();
  }, []);

  const handleSave = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter an email address');
      return;
    }
    setSaving(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/settings/cancelled-renders-user`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: newEmail.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message);
        setCurrentAccount(data);
        setNewEmail('');
      } else {
        toast.error(data.detail || 'Failed to update');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
    setSaving(false);
  };

  return (
    <div>
      {loading ? (
        <div className="flex items-center gap-2 text-zinc-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      ) : (
        <>
          <div className="mb-4 p-3 bg-black/20 rounded-lg">
            <p className="text-zinc-400 text-sm">Current Account:</p>
            <p className="text-white font-medium">
              {currentAccount?.email || currentAccount?.user_id || 'Not set (using system default)'}
            </p>
            {currentAccount?.name && (
              <p className="text-zinc-500 text-sm">{currentAccount.name}</p>
            )}
          </div>
          
          <div className="flex gap-3">
            <Input
              placeholder="Enter user email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="input-dark flex-1"
            />
            <Button
              onClick={handleSave}
              disabled={saving || !newEmail.trim()}
              className="btn-primary"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Set Account'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

// Changelog manifest - add new entries here when deploying updates
const CHANGELOGS = [
  {
    date: '2026-05-27b',
    version: 'v2.9.62',
    title: '"Sent N×" badge appears on the Send Again button per gift. Counts only successful re-shares. New "Most Shared" sort option (7th) — heavy users can see which gifts are working.',
    file: '/changelogs/CHANGELOG_2026-05-27b.md',
  },
  {
    date: '2026-05-27',
    version: 'v2.9.61',
    title: 'Sort toggle added to the "My Gifts" page — 6 options (Newest / Oldest / Recipient A→Z·Z→A / Song or Video A→Z·Z→A). Choice persisted in localStorage.',
    file: '/changelogs/CHANGELOG_2026-05-27.md',
  },
  {
    date: '2026-05-25b',
    version: 'v2.9.60',
    title: '🚨 Hotfix: SUNO API CERTIFICATE_VERIFY_FAILED. Added SUNO_TLS_VERIFY env-var toggle so ops can flip OFF strict verify while SUNO fixes their Cloudflare Origin Certificate misconfiguration. Defensive CA bundle pinning to certifi for all outbound TLS.',
    file: '/changelogs/CHANGELOG_2026-05-25b.md',
  },
  {
    date: '2026-05-25',
    version: 'v2.9.59',
    title: 'Two production bug fixes: (1) Bounce-out to empty dashboard mid-render — caused by SongDetail/VideoDetail navigating away on any non-401 error during the 10s poll. Now stays put on 5xx/network errors. (2) Duplicate (Alt) songs — SUNO occasionally returns the same item twice; webhook + poll paths now skip sibling creation when audio_urls match.',
    file: '/changelogs/CHANGELOG_2026-05-25.md',
  },
  {
    date: '2026-05-20i',
    version: 'v2.9.58',
    title: '"Export ZIP (migration)" button in Admin → Backups. One JSONL per collection + manifest, ready to mongoimport directly into a self-hosted MongoDB (HP server migration).',
    file: '/changelogs/CHANGELOG_2026-05-20i.md',
  },
  {
    date: '2026-05-20h',
    version: 'v2.9.57',
    title: 'Default preset — star any saved preset and it auto-applies on the next cold page load. Works on both Dashboard + Library scopes; only one default per scope at a time.',
    file: '/changelogs/CHANGELOG_2026-05-20h.md',
  },
  {
    date: '2026-05-20g',
    version: 'v2.9.56',
    title: 'Fix: Mood + Genre dropdown options had a white background that made the white text unreadable until hover. Applied dark option styling across Dashboard, SongsSidebar, and Library.',
    file: '/changelogs/CHANGELOG_2026-05-20g.md',
  },
  {
    date: '2026-05-20f',
    version: 'v2.9.55',
    title: 'Filter presets added to the Dashboard "Your Songs" toolbar. Save combos of pill + Mood + Genre + Search and one-click recall. Reuses the Library preset backend with a `scope=dashboard` flag.',
    file: '/changelogs/CHANGELOG_2026-05-20f.md',
  },
  {
    date: '2026-05-20e',
    version: 'v2.9.54',
    title: 'Save filter presets on the public Library. Name any combo (Mood + Genre + Sort + Category + Search) and one-click recall it later. Synced server-side, capped at 10 per user with FIFO eviction.',
    file: '/changelogs/CHANGELOG_2026-05-20e.md',
  },
  {
    date: '2026-05-20d',
    version: 'v2.9.53',
    title: 'Mood + Genre dropdowns added inline to the public Library "All Songs" header. Shares state with the existing centered filter row.',
    file: '/changelogs/CHANGELOG_2026-05-20d.md',
  },
  {
    date: '2026-05-20c',
    version: 'v2.9.52',
    title: 'Replaced the auto-generated keyword chips with proper Mood + Genre dropdowns (same options as Create Song). Filters AND with the pill + search. Mirrored in the right sidebar.',
    file: '/changelogs/CHANGELOG_2026-05-20c.md',
  },
  {
    date: '2026-05-20b',
    version: 'v2.9.51',
    title: 'Sort toggle (Newest · Oldest · A→Z · Z→A) added to the public Library page for parity with the right SongsSidebar.',
    file: '/changelogs/CHANGELOG_2026-05-20b.md',
  },
  {
    date: '2026-05-20',
    version: 'v2.9.50',
    title: 'Song Organization — Favorites pill, Archive pill, prompt-keyword search chips, and free-text search across title/prompt/style. Mirrored in the right sidebar so heavy users can pin/archive without leaving the song detail page.',
    file: '/changelogs/CHANGELOG_2026-05-20.md',
  },
  {
    date: '2026-05-14a',
    version: 'v2.9.31',
    title: 'HP audio storage health tools (Admin → Client Errors). Audit + repair + transfer to bulk-recover songs whose audio_url still points at SUNO. Most "expired" songs are actually already on HP.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-13d',
    version: 'v2.9.30',
    title: 'SUNO background poller interval slowed 30s → 5min. Now that webhooks handle real-time completions, the poller is purely a safety net.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-13c',
    version: 'v2.9.29',
    title: 'Audit: applied the v2.9.28 auth-race fix to SongDetail, Library, and SongsSidebar. No more "bounce to login" or "empty list" on cold loads / back navigation anywhere.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-13b',
    version: 'v2.9.28',
    title: 'Fixed recurring "Dashboard shows 0 songs after navigating back" bug — auth race condition on cold loads no longer wipes the song/video list.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09aa',
    version: 'v2.9.27',
    title: 'Added V4.5+ (V4_5PLUS) as third SUNO model option — enhanced V4.5 with Personas, Covers, and vocals co-creation features.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09z',
    version: 'v2.9.26',
    title: '🎉 SUNO webhooks live. Songs now auto-complete the moment SUNO finishes (~30s) instead of waiting on polling. Eliminates the entire false-failure class.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09y',
    version: 'v2.9.25',
    title: '"Recover from SUNO" on song detail page now prompts for task_id when none stored. + Cover title no longer falls to ugly "aud_..." filename.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09x',
    version: 'v2.9.24',
    title: 'CRITICAL: Song library was silently truncated at 100 songs. Lifted cap to 2000 — your full library is back.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09w',
    version: 'v2.9.23',
    title: '"Variations" dropdown on parent songs — lists every regenerated child with thumbnail, status, and timestamp. One-click to A/B compare.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09v',
    version: 'v2.9.22',
    title: '"Regenerated from → View original" link on regenerated songs. Click to jump back to the parent for A/B comparing edits.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09u',
    version: 'v2.9.21',
    title: 'Render Another Variation now routes to Create Song with everything pre-filled — edit lyrics, style, voice, and model before re-rendering. Source MP3 is preserved.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09t',
    version: 'v2.9.20',
    title: 'Admin "Recover from SUNO" button now appears directly on any failed song detail page — one click instead of pasting song_id into Admin Panel',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09s',
    version: 'v2.9.19',
    title: 'Admin → Client Errors → Recover failed song: pulls audio from SUNO and marks the song completed. Rescues victims of the v2.9.18 race or backend outages.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09r',
    version: 'v2.9.18',
    title: 'CRITICAL regen race-condition fix: Render Another Variation / Regenerate was being marked failed before background SUNO submit could complete',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09q',
    version: 'v2.9.17',
    title: 'Cover transcription is now async — POST returns instantly with job_id, frontend polls until done. Eliminates 504 timeouts on longer source MP3s.',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09p',
    version: 'v2.9.16',
    title: 'GPT-4o-mini lyric formatter rewritten as a strict "passive labeler" — no more line duplication, splitting verses incorrectly, or dropped chorus lines',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09o',
    version: 'v2.9.15',
    title: 'Client Errors database — ErrorBoundary now logs every render crash to MongoDB; new Admin → Client Errors tab to browse them dated/timed with full stack traces',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09n',
    version: 'v2.9.14',
    title: 'App-wide ErrorBoundary — render crashes now show a friendly fallback with Refresh + Dashboard buttons instead of a blank page',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09m',
    version: 'v2.9.13',
    title: '🚨 CRITICAL FIX: Create Song page was blank since v2.9.8 (TDZ ReferenceError on coverMode) — moved auto-advance useEffect after state declarations',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09l',
    version: 'v2.9.12',
    title: 'Cover lyrics — strip spoken outros ("thanks for watching", "subscribe", etc.) before sending to SUNO',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09k',
    version: 'v2.9.11',
    title: 'Cover songs no longer show "Untitled Song" while rendering — derived title now persisted to DB at submit-time',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09',
    version: 'v2.9.10',
    title: 'Cover lyrics auto-formatted with [Verse]/[Chorus]/[Bridge] section markers via GPT-4o-mini',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09i',
    version: 'v2.9.9',
    title: 'CRITICAL Cover Song fixes — always custom-mode + inline Whisper fallback (no more unrelated junk renders) · transcription blocks Generate · Alt variations inherit cover flags',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09h',
    version: 'v2.9.8',
    title: 'Cover mode auto-advances past Lyrics step in Advanced flow',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09g',
    version: 'v2.9.7',
    title: 'Cover upload — fix HTTP 504 (multipart streaming to HP, ~3× faster, HP v2.2.1)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09f',
    version: 'v2.9.6',
    title: 'Cover sources now count toward storage quotas (auto-migration on deploy)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09e',
    version: 'v2.9.5',
    title: 'Cover Songs — "Render Another Variation" (one-click re-render with same source MP3)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09d',
    version: 'v2.9.4',
    title: 'Cover upload — fix HTTP 520 timeout (split upload + transcribe into 2 requests)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09c',
    version: 'v2.9.3',
    title: 'Cover Song — auto-transcribe lyrics with Whisper · fixes placeholder-text-as-lyrics bug',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09b',
    version: 'v2.9.2',
    title: 'Cover Song — one-click upload & go (style/title/lyrics now all optional; SUNO auto-fills on delivery)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-09a',
    version: 'v2.9.1',
    title: 'Cover Song mode — upload MP3 → SUNO re-records in your style (compatible with Instrumental toggle)',
    file: '/changelogs/CHANGELOG_2026-05-09.md',
  },
  {
    date: '2026-05-08b',
    version: 'v2.9.0',
    title: 'Audio mixing fully wired: render crash fix · per-clip audio toggle · global duck-song toggle (HP v2.2.0)',
    file: '/changelogs/CHANGELOG_2026-05-08b.md',
  },
  {
    date: '2026-05-08',
    version: 'v2.8.5',
    title: 'Grok Imagine returns (Admin Studio beta) · AI clips count toward quotas · "View as user" toggle · SUNO banner',
    file: '/changelogs/CHANGELOG_2026-05-08.md',
  },
  {
    date: '2026-05-07b',
    version: 'v2.8.0',
    title: 'My AI Clips library — auto-saved clips, recent tab, auto-download',
    file: '/changelogs/CHANGELOG_2026-05-07b.md',
  },
  {
    date: '2026-05-07',
    version: 'v2.7.1',
    title: 'Optional song + auto-duck audio mix · preview clip audio plays',
    file: '/changelogs/CHANGELOG_2026-05-07.md',
  },
  {
    date: '2026-05-06g',
    version: 'v2.7.0',
    title: 'Daily DB Backup System (admin panel + auto-rotation)',
    file: '/changelogs/CHANGELOG_2026-05-06g.md',
  },
  {
    date: '2026-05-06f',
    version: 'v2.6.7',
    title: 'Avatar Lip-Sync — image also uploaded to Atlas CDN',
    file: '/changelogs/CHANGELOG_2026-05-06f.md',
  },
  {
    date: '2026-05-06e',
    version: 'v2.6.6',
    title: 'Avatar Lip-Sync — END-TO-END WORKING (schema + audio trim + poll fix)',
    file: '/changelogs/CHANGELOG_2026-05-06e.md',
  },
  {
    date: '2026-05-06d',
    version: 'v2.6.5',
    title: 'Wan 2.2 Spicy ref-image fix · Avatar uploadMedia response shape fix',
    file: '/changelogs/CHANGELOG_2026-05-06d.md',
  },
  {
    date: '2026-05-06c',
    version: 'v2.6.4',
    title: 'Admin Studio: Wan 2.2 Spicy added',
    file: '/changelogs/CHANGELOG_2026-05-06c.md',
  },
  {
    date: '2026-05-06b',
    version: 'v2.6.3',
    title: 'Avatar Lip-Sync — final Atlas upload fix',
    file: '/changelogs/CHANGELOG_2026-05-06b.md',
  },
  {
    date: '2026-05-06',
    version: 'v2.6.2',
    title: 'Birthday Save Fix, Avatar Lip-Sync Fix, Prompt Tag Safety Net',
    file: '/changelogs/CHANGELOG_2026-05-06.md',
  },
  {
    date: '2026-05-05',
    version: 'v2.6.1',
    title: 'Quick-Preview Popups, Keyboard Shortcuts, Manual Crop Toggle',
    file: '/changelogs/CHANGELOG_2026-05-05.md',
  },
  {
    date: '2026-05-03',
    version: 'v2.6.0',
    title: 'Admin Studio (Phase 1), Vidu Cleanup, Engine Polish',
    file: '/changelogs/CHANGELOG_2026-05-03.md',
  },
  {
    date: '2026-05-02',
    version: 'v2.5.0',
    title: 'Atlas Cloud, Pro Lyrics, Stability & False-Failure Fixes',
    file: '/changelogs/CHANGELOG_2026-05-02.md',
  },
  {
    date: '2026-04-13',
    version: 'v2.0',
    title: 'SUNO V5, Dual Variations, Cancel/Restore System',
    file: '/changelogs/CHANGELOG_2026-04-13.md',
  },
  {
    date: '2026-04-08',
    version: 'v1.9',
    title: 'HP Server Migration, Public Library, Covers & Downloads',
    file: '/changelogs/CHANGELOG_2026-04-08.md',
  },
  {
    date: '2026-03-28',
    version: 'v1.7',
    title: 'Slideshow Videos, Birthday Songs, Gift Sharing',
    file: '/changelogs/CHANGELOG_2026-03-28.md',
  },
  {
    date: '2026-03-19',
    version: 'v1.5',
    title: 'AI Lyrics, Feedback System, Admin User Tools',
    file: '/changelogs/CHANGELOG_2026-03-19.md',
  },
];

const ChangelogViewer = () => {
  const [selectedLog, setSelectedLog] = useState(null);
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);

  const openChangelog = async (log) => {
    setSelectedLog(log);
    setLoading(true);
    setContent('');
    try {
      const res = await fetch(log.file);
      if (res.ok) {
        const text = await res.text();
        setContent(text);
      } else {
        setContent('Failed to load changelog.');
      }
    } catch {
      setContent('Failed to load changelog.');
    }
    setLoading(false);
  };

  return (
    <div className="glass-card p-6" data-testid="changelog-section">
      <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
        <FileText className="h-5 w-5 text-green-400" />
        Changelogs
      </h3>
      <p className="text-zinc-400 text-sm mb-4">
        Session updates, feature additions, and system changes.
      </p>

      <div className="space-y-2">
        {CHANGELOGS.map((log) => (
          <button
            key={log.file}
            data-testid={`changelog-item-${log.date}`}
            onClick={() => openChangelog(log)}
            className="w-full flex items-center justify-between p-3 bg-black/20 rounded-lg hover:bg-white/5 transition-colors text-left group"
          >
            <div className="flex items-center gap-3">
              <FileText className="h-4 w-4 text-green-400 shrink-0" />
              <div>
                <p className="text-white font-medium text-sm">{log.title}</p>
                <p className="text-zinc-500 text-xs">{log.date} &middot; {log.version}</p>
              </div>
            </div>
            <ExternalLink className="h-4 w-4 text-zinc-600 group-hover:text-zinc-400 transition-colors shrink-0" />
          </button>
        ))}
      </div>

      {/* Changelog Detail Dialog */}
      <Dialog open={!!selectedLog} onOpenChange={(open) => { if (!open) setSelectedLog(null); }}>
        <DialogContent className="max-w-3xl max-h-[80vh] bg-zinc-900 border-zinc-700 text-white overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white text-lg">
              {selectedLog?.title}
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              {selectedLog?.date} &middot; {selectedLog?.version}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-y-auto flex-1 pr-2 changelog-content">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
              </div>
            ) : (
              <ReactMarkdown
                components={{
                  h1: ({children}) => <h1 className="text-2xl font-bold text-white mt-6 mb-3 first:mt-0">{children}</h1>,
                  h2: ({children}) => <h2 className="text-xl font-bold text-purple-300 mt-5 mb-2">{children}</h2>,
                  h3: ({children}) => <h3 className="text-base font-semibold text-zinc-200 mt-4 mb-1">{children}</h3>,
                  p: ({children}) => <p className="text-zinc-300 text-sm leading-relaxed mb-2">{children}</p>,
                  ul: ({children}) => <ul className="list-disc list-inside text-zinc-300 text-sm space-y-1 mb-3 ml-2">{children}</ul>,
                  ol: ({children}) => <ol className="list-decimal list-inside text-zinc-300 text-sm space-y-1 mb-3 ml-2">{children}</ol>,
                  li: ({children}) => <li className="text-zinc-300 text-sm">{children}</li>,
                  strong: ({children}) => <strong className="text-white font-semibold">{children}</strong>,
                  code: ({children}) => <code className="bg-black/40 text-green-400 text-xs px-1.5 py-0.5 rounded">{children}</code>,
                  hr: () => <hr className="border-zinc-700 my-4" />,
                  table: ({children}) => <div className="overflow-x-auto mb-3"><table className="w-full text-sm border-collapse">{children}</table></div>,
                  thead: ({children}) => <thead className="border-b border-zinc-700">{children}</thead>,
                  th: ({children}) => <th className="text-left text-zinc-400 text-xs font-medium p-2">{children}</th>,
                  td: ({children}) => <td className="text-zinc-300 text-xs p-2 border-t border-zinc-800">{children}</td>,
                }}
              >
                {content}
              </ReactMarkdown>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};


export default function AdminPanel() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('stats');

  // Client error log state (Bug-tab content)
  const [clientErrors, setClientErrors] = useState([]);
  const [clientErrorsLoading, setClientErrorsLoading] = useState(false);
  const [clientErrorsTotal, setClientErrorsTotal] = useState(0);
  const [expandedErrorId, setExpandedErrorId] = useState(null);
  const [confirmClearErrors, setConfirmClearErrors] = useState(false);

  // Recover-song tool state (lives in same Bug tab)
  const [recoverSongIdInput, setRecoverSongIdInput] = useState('');
  const [recoverTaskIdInput, setRecoverTaskIdInput] = useState('');
  const [recoverUserIdInput, setRecoverUserIdInput] = useState('');
  const [recovering, setRecovering] = useState(false);
  const [recoverResult, setRecoverResult] = useState(null);

  // HP audio storage health tools state (Audit / Repair / Transfer from SUNO).
  // These were referenced in JSX + handlers without being declared, which
  // raised `ReferenceError: hpAuditing is not defined` the moment the Client
  // Errors tab tried to render — the ErrorBoundary caught it and showed the
  // "page crashed before it could render" fallback. v2.9.32.2 fix.
  const [hpAuditing, setHpAuditing] = useState(false);
  const [hpAuditResult, setHpAuditResult] = useState(null);
  const [hpRepairing, setHpRepairing] = useState(false);
  const [hpRepairResult, setHpRepairResult] = useState(null);

  // "View as regular user" — when ON, hides admin-only AI engines from the
  // AI Generate modal so admins can preview exactly what regular users see.
  // Stored in localStorage (per-browser). The AIGenerateModal reads the same
  // key on every render and a `storage` event triggers a cross-tab refresh.
  const [viewAsUser, setViewAsUser] = useState(() => {
    try { return localStorage.getItem('wim_admin_view_as_user') === '1'; } catch { return false; }
  });
  const toggleViewAsUser = (next) => {
    setViewAsUser(next);
    try {
      localStorage.setItem('wim_admin_view_as_user', next ? '1' : '0');
      // Manually broadcast so the AIGenerateModal in the SAME tab re-reads
      // (the native `storage` event only fires across tabs).
      window.dispatchEvent(new Event('wim_admin_view_as_user_changed'));
    } catch { /* ignore */ }
  };

  // DB backup state
  const [dbBackups, setDbBackups] = useState([]);
  const [dbBackupRetention, setDbBackupRetention] = useState(7);
  const [dbBackupBusy, setDbBackupBusy] = useState(false);

  const loadDbBackups = useCallback(async () => {
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/db-backup/list`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      setDbBackups(data.backups || []);
      setDbBackupRetention(data.retention_count || 7);
    } catch (e) { /* ignore */ }
  }, []);
  const triggerDbBackup = async () => {
    if (dbBackupBusy) return;
    setDbBackupBusy(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/db-backup/run-now`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const docTotal = Object.values(data.counts || {}).filter(n => n > 0).reduce((a, b) => a + b, 0);
      toast.success(`Backup created: ${data.filename} (${docTotal.toLocaleString()} docs)`);
      await loadDbBackups();
    } catch (e) {
      toast.error('Backup failed — check console');
      console.error(e);
    } finally {
      setDbBackupBusy(false);
    }
  };
  const downloadDbBackup = async (filename) => {
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/db-backup/download/${encodeURIComponent(filename)}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      toast.error('Download failed');
      console.error(e);
    }
  };
  const deleteDbBackup = async (filename) => {
    if (!window.confirm(`Delete backup ${filename}? This cannot be undone.`)) return;
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/db-backup/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      toast.success(`Deleted ${filename}`);
      await loadDbBackups();
    } catch (e) {
      toast.error('Delete failed');
    }
  };
  const [stats, setStats] = useState(null);
  const [aiEngineStats, setAiEngineStats] = useState(null);
  const [users, setUsers] = useState([]);
  // Click-to-sort state for the Users table (v2.9.40). Server fetch keeps
  // the original ordering; sorting happens client-side so each header
  // click is instant and supports three states per column: unsorted →
  // ascending → descending → unsorted. We default to ascending-by-User
  // so the list is always alphabetical on first paint regardless of how
  // the backend happens to sort null/empty names.
  const [userSort, setUserSort] = useState({ key: 'user', dir: 'asc' });
  const [userSearch, setUserSearch] = useState('');
  const [transactions, setTransactions] = useState([]);
  // v2.9.42 — same click-to-sort UX applied to transactions for consistency
  // with the Users table. Default = newest first (Date desc).
  const [txnSort, setTxnSort] = useState({ key: 'date', dir: 'desc' });
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  // Editable settings
  const [packages, setPackages] = useState([]);
  const [costs, setCosts] = useState({});

  // Credit edit modal state
  const [editingUser, setEditingUser] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditMode, setCreditMode] = useState('set'); // 'set', 'add', 'subtract'
  const [updatingCredits, setUpdatingCredits] = useState(false);

  // Import-MP3 modal state — admin-only tool to bring an external MP3
  // into a chosen user's library as a completed song. Kept off the
  // public UI so copyright liability stays with the admin who imported.
  const [importSongUser, setImportSongUser] = useState(null);
  const [importSongForm, setImportSongForm] = useState({
    title: '', artist: '', lyrics: '', genre: '', mood: '', transcribe: false,
  });
  const [importAudioFile, setImportAudioFile] = useState(null);
  const [importCoverFile, setImportCoverFile] = useState(null);
  const [importingSong, setImportingSong] = useState(false);

  // Promo Codes tab state
  const [promoCampaigns, setPromoCampaigns] = useState([]);
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoExpanded, setPromoExpanded] = useState(null); // campaign_id whose detail is open
  const [promoCampaignDetail, setPromoCampaignDetail] = useState(null);
  const [promoSummary, setPromoSummary] = useState(null);
  const [promoDigest, setPromoDigest] = useState(null);
  const [showPromoBatchModal, setShowPromoBatchModal] = useState(false);
  // Sort / filter controls for the campaigns list. All client-side —
  // campaigns fit comfortably in memory (dozens, not thousands).
  const [promoSearch, setPromoSearch] = useState('');
  const [promoStatusFilter, setPromoStatusFilter] = useState('all'); // 'all' | 'active' | 'expired' | 'depleted'
  const [promoSort, setPromoSort] = useState('newest'); // newest | oldest | rate_desc | rate_asc | codes_desc | expires_soon
  const [newBatch, setNewBatch] = useState({
    campaign_name: '',
    campaign_tag: '',
    credits: 75,
    count: 10,
    expires_in_days: 180,
    influencer_handle: '',
  });
  const [batchResult, setBatchResult] = useState(null);
  const [creatingBatch, setCreatingBatch] = useState(false);

  // Prompt tags state
  const [promptTags, setPromptTags] = useState([]);
  const [promptCategories, setPromptCategories] = useState({});
  const [activePromptCategory, setActivePromptCategory] = useState('vocals');
  const [newTag, setNewTag] = useState({ 
    tag: '', 
    description: '', 
    use_case: '', 
    category: 'vocals',
    subcategory: 'gender',
    example: ''
  });
  const [addingTag, setAddingTag] = useState(false);
  const [deletingTagId, setDeletingTagId] = useState(null);
  const [editingTag, setEditingTag] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Delete confirmation state
  const [deleteTagDialog, setDeleteTagDialog] = useState({ open: false, tagId: null, tagName: '' });

  // Song options state (moods/genres)
  const [songMoods, setSongMoods] = useState([]);
  const [songGenres, setSongGenres] = useState([]);
  const [newMood, setNewMood] = useState('');
  const [newGenre, setNewGenre] = useState('');
  const [savingSongOptions, setSavingSongOptions] = useState(false);

  // Song management state
  const [viewingSongsUser, setViewingSongsUser] = useState(null);
  const [userSongs, setUserSongs] = useState([]);
  const [loadingUserSongs, setLoadingUserSongs] = useState(false);
  const [reassignDialog, setReassignDialog] = useState({ open: false, song: null });
  const [editSongDialog, setEditSongDialog] = useState({ open: false, song: null });
  const [targetUserId, setTargetUserId] = useState('');
  const [reassigning, setReassigning] = useState(false);

  // Delete user state
  const [deleteUserDialog, setDeleteUserDialog] = useState({ open: false, user: null });
  const [deletingUser, setDeletingUser] = useState(false);

  // Feedback state
  const [feedbackList, setFeedbackList] = useState([]);
  const [loadingFeedback, setLoadingFeedback] = useState(false);
  const [deletingFeedbackId, setDeletingFeedbackId] = useState(null);
  const [confirmDeleteFeedbackId, setConfirmDeleteFeedbackId] = useState(null);

  // Birthday name requests state
  const [nameRequests, setNameRequests] = useState({ requests: [], total_unique_names: 0, total_requests: 0 });
  const [loadingNameRequests, setLoadingNameRequests] = useState(false);

  // Song Health state
  const [songHealth, setSongHealth] = useState(null);
  const [allSongs, setAllSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(false);
  // v2.9.42 — click-to-sort applied to the Song Health table for consistency
  // with the Users and Transactions tabs. Default = unsorted (server returns
  // newest first which is the natural default for triage).
  const [songSort, setSongSort] = useState({ key: null, dir: null });
  const [songSearchQuery, setSongSearchQuery] = useState('');
  const [showBrokenOnly, setShowBrokenOnly] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [editingSongData, setEditingSongData] = useState(null);
  const [savingSong, setSavingSong] = useState(false);
  const [testingAudioId, setTestingAudioId] = useState(null);
  
  // Audio player state for user songs list
  const [playingSongId, setPlayingSongId] = useState(null);
  const userSongsAudioRef = React.useRef(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResults, setMigrationResults] = useState(null);
  // Image-generation cost tracker
  const [imageGenCost, setImageGenCost] = useState(null);
  const [musicgptStats, setMusicgptStats] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, usersRes, txnRes, settingsRes, tagsRes, catsRes, aiStatsRes, imgCostRes, mgptRes] = await Promise.all([
        nativeFetchWithAuth(`${API}/admin/stats`),
        nativeFetchWithAuth(`${API}/admin/users?limit=200&sort_by=name`),
        nativeFetchWithAuth(`${API}/admin/transactions?limit=500`),
        nativeFetchWithAuth(`${API}/admin/settings`),
        nativeFetchWithAuth(`${API}/prompt-tags`),
        nativeFetchWithAuth(`${API}/prompt-tags/categories`),
        nativeFetchWithAuth(`${API}/admin/ai-engine-stats`),
        nativeFetchWithAuth(`${API}/admin/image-gen-cost`),
        nativeFetchWithAuth(`${API}/admin/musicgpt-stats`),
      ]);

      if (statsRes.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
        return;
      }

      // v2.9.34 — When ANY of the admin endpoints come back 401 we know the
      // session cookie has expired (even if the localStorage token is still
      // alive). Without this guard the page sits on the loading spinner
      // forever because `stats` never populates. Bounce the user to /login.
      const sessionExpired = [statsRes, usersRes, txnRes, settingsRes, mgptRes].some(
        (r) => r && r.status === 401
      );
      if (sessionExpired) {
        toast.error('Your session expired — please sign in again.');
        navigate('/login');
        return;
      }

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (txnRes.ok) setTransactions(await txnRes.json());
      if (tagsRes.ok) setPromptTags(await tagsRes.json());
      if (catsRes.ok) setPromptCategories(await catsRes.json());
      if (aiStatsRes.ok) setAiEngineStats(await aiStatsRes.json());
      if (imgCostRes.ok) setImageGenCost(await imgCostRes.json());
      if (mgptRes.ok) setMusicgptStats(await mgptRes.json());
      if (settingsRes.ok) {
        const data = await settingsRes.json();
        setSettings(data);
        setPackages(data.credit_packages || []);
        setCosts(data.credit_costs || {});
      }
      
      // Fetch song options
      const songOptsResult = await apiFetch('/admin/song-options');
      if (songOptsResult.data) {
        const opts = songOptsResult.data;
        setSongMoods((opts.moods || []).sort());
        setSongGenres((opts.genres || []).sort());
      }
      
      // Fetch feedback
      const feedbackRes = await nativeFetchWithAuth(`${API}/admin/feedback`, { credentials: 'include' });
      if (feedbackRes.ok) {
        setFeedbackList(await feedbackRes.json());
      }
      
      // Fetch birthday name requests
      const nameReqRes = await nativeFetchWithAuth(`${API}/admin/birthday-name-requests`, { credentials: 'include' });
      if (nameReqRes.ok) {
        setNameRequests(await nameReqRes.json());
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSavePackages = async () => {
    setSaving(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/settings/packages`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(packages)
      });
      if (res.ok) {
        toast.success('Credit packages updated!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save packages');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCosts = async () => {
    setSaving(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/settings/costs`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(costs)
      });
      if (res.ok) {
        toast.success('Credit costs updated!');
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error('Failed to save costs');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSongOptions = async () => {
    setSavingSongOptions(true);
    try {
      await apiFetch('/admin/song-options', {
        method: 'PUT',
        body: JSON.stringify({ moods: songMoods, genres: songGenres })
      });
      toast.success('Song options updated!');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save song options');
    } finally {
      setSavingSongOptions(false);
    }
  };

  const addMood = () => {
    if (newMood.trim() && !songMoods.includes(newMood.trim().toLowerCase())) {
      setSongMoods([...songMoods, newMood.trim().toLowerCase()].sort());
      setNewMood('');
    }
  };

  const removeMood = (mood) => {
    setSongMoods(songMoods.filter(m => m !== mood));
  };

  const addGenre = () => {
    if (newGenre.trim() && !songGenres.includes(newGenre.trim().toLowerCase())) {
      setSongGenres([...songGenres, newGenre.trim().toLowerCase()].sort());
      setNewGenre('');
    }
  };

  const removeGenre = (genre) => {
    setSongGenres(songGenres.filter(g => g !== genre));
  };

  const updatePackage = (index, field, value) => {
    const updated = [...packages];
    updated[index] = { ...updated[index], [field]: field === 'credits' || field === 'price' ? parseFloat(value) || 0 : value };
    setPackages(updated);
  };

  const updateCost = (key, value) => {
    setCosts({ ...costs, [key]: parseInt(value) || 0 });
  };

  // =============== Promo Codes Handlers ===============
  const loadPromoCampaigns = useCallback(async () => {
    setPromoLoading(true);
    try {
      // Fetch program summary, campaign list, and weekly digest in
      // parallel — three cheap queries, one round trip. Keeps the tab
      // feeling instant instead of rendering in three flickering stages.
      const [campaignsRes, summaryRes, digestRes] = await Promise.all([
        apiFetch('/admin/promo/campaigns'),
        apiFetch('/admin/promo/summary'),
        apiFetch('/admin/promo/weekly-digest'),
      ]);
      setPromoCampaigns(campaignsRes.data.campaigns || []);
      setPromoSummary(summaryRes.data || null);
      setPromoDigest(digestRes.data || null);
    } catch (err) {
      toast.error('Failed to load promo campaigns');
    } finally {
      setPromoLoading(false);
    }
  }, []);

  const loadPromoCampaignDetail = async (campaignId) => {
    try {
      const { data } = await apiFetch(`/admin/promo/campaign/${campaignId}`);
      setPromoCampaignDetail(data);
      setPromoExpanded(campaignId);
    } catch (err) {
      toast.error('Failed to load campaign detail');
    }
  };

  const handleGenerateBatch = async () => {
    if (!newBatch.campaign_name?.trim() || !newBatch.campaign_tag?.trim()) {
      toast.error('Campaign name and tag are required');
      return;
    }
    setCreatingBatch(true);
    setBatchResult(null);
    try {
      const { data } = await apiFetch('/admin/promo/generate', {
        method: 'POST',
        body: JSON.stringify(newBatch),
      });
      setBatchResult(data);
      toast.success(`Generated ${data.count} codes for "${data.campaign_name}"`);
      loadPromoCampaigns();
    } catch (err) {
      toast.error(err.message || 'Failed to generate batch');
    } finally {
      setCreatingBatch(false);
    }
  };

  const handlePauseCampaign = async (campaignId, campaignName) => {
    if (!window.confirm(`Pause all unredeemed codes in "${campaignName}"? This cannot be undone.`)) return;
    try {
      const { data } = await apiFetch(`/admin/promo/pause-campaign/${campaignId}`, { method: 'POST' });
      toast.success(`Revoked ${data.codes_revoked} unused codes`);
      loadPromoCampaigns();
      if (promoExpanded === campaignId) loadPromoCampaignDetail(campaignId);
    } catch (err) {
      toast.error('Failed to pause campaign');
    }
  };

  const handleDownloadCsv = async (campaignId, campaignName) => {
    try {
      const token = localStorage.getItem('wim_session_token');
      const res = await nativeFetchWithAuth(`${API}/admin/promo/export/${campaignId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `promo-${campaignName.replace(/\s+/g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  };

  const handleCopyAllCodes = async (codes) => {
    const text = (codes || []).map(c => c.code).join('\n');
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`Copied ${codes.length} codes to clipboard`);
    } catch (err) {
      toast.error('Copy failed — your browser may block clipboard access');
    }
  };

  // Client error log loader
  const loadClientErrors = useCallback(async () => {
    setClientErrorsLoading(true);
    try {
      const { data } = await apiFetch('/admin/client-errors?limit=200');
      setClientErrors(data?.errors || []);
      setClientErrorsTotal(data?.total || 0);
    } catch (err) {
      toast.error(err?.message || 'Failed to load client errors');
    } finally {
      setClientErrorsLoading(false);
    }
  }, []);

  const handleClearClientErrors = async () => {
    try {
      const { data } = await apiFetch('/admin/client-errors', { method: 'DELETE' });
      toast.success(`Cleared ${data?.deleted ?? 0} error logs`);
      setConfirmClearErrors(false);
      loadClientErrors();
    } catch (err) {
      toast.error(err?.message || 'Failed to clear errors');
    }
  };

  const handleHpAudit = async () => {
    setHpAuditing(true);
    setHpAuditResult(null);
    try {
      const { data } = await apiFetch('/admin/audit-hp-files', { method: 'POST' });
      setHpAuditResult(data);
      toast.success(`Audit complete: ${data?.summary?.total_suno_urls_in_db || 0} songs checked`);
    } catch (err) {
      toast.error(err?.message || 'Audit failed');
    } finally {
      setHpAuditing(false);
    }
  };

  const handleHpRepair = async () => {
    if (!window.confirm('Re-point all "already-on-HP" songs to their HP URLs?')) return;
    setHpRepairing(true);
    setHpRepairResult(null);
    try {
      const { data } = await apiFetch('/admin/repair-hp-pointers', { method: 'POST' });
      setHpRepairResult(data);
      toast.success(`Repaired ${data?.repaired_count || 0} song URL pointers`);
    } catch (err) {
      toast.error(err?.message || 'Repair failed');
    } finally {
      setHpRepairing(false);
    }
  };

  const handleRecoverSong = async () => {
    const songId = recoverSongIdInput.trim();
    const taskId = recoverTaskIdInput.trim();
    if (!songId && !taskId) {
      toast.error('Enter either a song_id or a SUNO task_id');
      return;
    }
    setRecovering(true);
    setRecoverResult(null);
    try {
      const body = {};
      if (songId) body.song_id = songId;
      if (taskId) body.task_id = taskId;
      if (recoverUserIdInput.trim()) body.user_id = recoverUserIdInput.trim();
      const { data } = await apiFetch('/admin/recover-song', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      setRecoverResult(data);
      if (data?.result === 'recovered') {
        toast.success(`Recovered: ${data.title || data.song_id}`);
      } else {
        toast.info(data?.message || data?.result || 'See result below');
      }
    } catch (err) {
      toast.error(err?.message || 'Recovery failed');
      setRecoverResult({ result: 'error', message: err?.message || 'Recovery failed' });
    } finally {
      setRecovering(false);
    }
  };

  // Load campaigns when tab becomes active
  useEffect(() => {
    if (activeTab === 'promos') loadPromoCampaigns();
    if (activeTab === 'backups') loadDbBackups();
    if (activeTab === 'errors') loadClientErrors();
  }, [activeTab, loadPromoCampaigns, loadDbBackups, loadClientErrors]);

  const handleEditCredits = (user) => {
    setEditingUser(user);
    setCreditAmount(user.credits.toString());
    setCreditMode('set');
  };

  const handleUpdateCredits = async () => {
    if (!editingUser || creditAmount === '') return;
    
    setUpdatingCredits(true);
    try {
      let newCredits = parseInt(creditAmount) || 0;
      
      if (creditMode === 'add') {
        newCredits = editingUser.credits + newCredits;
      } else if (creditMode === 'subtract') {
        newCredits = Math.max(0, editingUser.credits - newCredits);
      }
      
      const res = await nativeFetchWithAuth(`${API}/admin/users/${editingUser.user_id}/credits?credits=${newCredits}`, {
        method: 'PUT',
        credentials: 'include',
      });
      
      if (res.ok) {
        toast.success(`Credits updated to ${newCredits} for ${editingUser.name}`);
        setEditingUser(null);
        setCreditAmount('');
        fetchData(); // Refresh the users list
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update credits');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update credits');
    } finally {
      setUpdatingCredits(false);
    }
  };

  // Handle changing a user's role — cycles user → admin → super_admin
  // for super_admins, or just user ↔ admin (locked out server-side) for
  // regular admins (button won't render for them anyway).
  const handleChangeRole = async (targetUser, newRole) => {
    if (!newRole || newRole === targetUser.role) return;
    if (!window.confirm(`Change ${targetUser.name}'s role to "${newRole}"?`)) return;
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/users/${targetUser.user_id}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      if (res.ok) {
        toast.success(`${targetUser.name} is now ${newRole}`);
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update role');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update role');
    }
  };

  // Backwards-compat wrapper for the existing "Toggle role" button in the
  // Users tab. If the caller is a super_admin, we still cycle through
  // user → admin (no super_admin — that's set via the dedicated dropdown
  // below to prevent accidental promotion).
  const handleToggleRole = async (targetUser) => {
    const newRole = targetUser.role === 'admin' || targetUser.role === 'super_admin' ? 'user' : 'admin';
    return handleChangeRole(targetUser, newRole);
  };

  // Song management handlers
  const handleViewUserSongs = async (u) => {
    setViewingSongsUser(u);
    setLoadingUserSongs(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/users/${u.user_id}/songs`, { credentials: 'include' });
      if (res.ok) {
        setUserSongs(await res.json());
      } else {
        toast.error('Failed to load user songs');
      }
    } catch (error) {
      toast.error('Failed to load user songs');
    } finally {
      setLoadingUserSongs(false);
    }
  };
  // Open the Import-MP3 modal, pre-targeted to this user. All the
  // heavy lifting lives in the backend endpoint — this side is just a
  // multipart FormData POST.
  const openImportSongModal = (user) => {
    setImportSongUser(user);
    setImportSongForm({ title: '', artist: '', lyrics: '', genre: '', mood: '', transcribe: false });
    setImportAudioFile(null);
    setImportCoverFile(null);
  };

  const submitImportSong = async () => {
    if (!importSongUser) return;
    if (!importAudioFile) { toast.error('Please choose an audio file'); return; }
    if (!importSongForm.title.trim()) { toast.error('Please enter a title'); return; }
    setImportingSong(true);
    try {
      const fd = new FormData();
      fd.append('target_user_id', importSongUser.user_id);
      fd.append('title', importSongForm.title.trim());
      if (importSongForm.artist.trim()) fd.append('artist', importSongForm.artist.trim());
      if (importSongForm.lyrics.trim()) fd.append('lyrics', importSongForm.lyrics);
      if (importSongForm.genre.trim()) fd.append('genre', importSongForm.genre.trim());
      if (importSongForm.mood.trim()) fd.append('mood', importSongForm.mood.trim());
      fd.append('transcribe', String(importSongForm.transcribe));
      fd.append('audio_file', importAudioFile);
      if (importCoverFile) fd.append('cover_image', importCoverFile);
      const res = await nativeFetchWithAuth(`${API}/admin/songs/import`, {
        method: 'POST', body: fd, credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Import failed (${res.status})`);
      }
      const data = await res.json();
      toast.success(
        `Imported "${importSongForm.title}" for ${importSongUser.email}` +
        (data.lyrics_transcribed ? ' · lyrics transcribed by our lyricists' : ''),
      );
      setImportSongUser(null);
    } catch (e) {
      toast.error(e.message || 'Import failed');
    } finally {
      setImportingSong(false);
    }
  };

  const handleReassignSong = async () => {
    if (!targetUserId.trim()) {
      toast.error('Please select a target user');
      return;
    }
    
    setReassigning(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/songs/${reassignDialog.song.song_id}/reassign?new_user_id=${targetUserId}`, {
        method: 'PUT',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Song reassigned to ${data.new_user_email}`);
        setReassignDialog({ open: false, song: null });
        setTargetUserId('');
        // Refresh the songs list
        if (viewingSongsUser) {
          handleViewUserSongs(viewingSongsUser);
        }
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to reassign song');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to reassign song');
    } finally {
      setReassigning(false);
    }
  };

  // Delete user handler (admin)
  const handleDeleteUser = async () => {
    if (!deleteUserDialog.user) return;
    
    setDeletingUser(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/users/${deleteUserDialog.user.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'User deleted successfully');
        setDeleteUserDialog({ open: false, user: null });
        fetchData(); // Refresh user list
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  // Prompt tag handlers
  const handleAddTag = async () => {
    if (!newTag.tag.trim() || !newTag.description.trim()) {
      toast.error('Tag and description are required');
      return;
    }
    
    setAddingTag(true);
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTag)
      });
      
      if (res.ok) {
        toast.success('Tag added successfully');
        setNewTag({ 
          tag: '', 
          description: '', 
          use_case: '', 
          category: activePromptCategory,
          subcategory: '',
          example: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to add tag');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to add tag');
    } finally {
      setAddingTag(false);
    }
  };

  const handleUpdateTag = async () => {
    if (!editingTag) return;
    
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags/${editingTag.tag_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          tag: editingTag.tag,
          description: editingTag.description,
          use_case: editingTag.use_case,
          category: editingTag.category,
          subcategory: editingTag.subcategory,
          example: editingTag.example
        })
      });
      
      if (res.ok) {
        toast.success('Tag updated');
        setEditingTag(null);
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to update tag');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to update tag');
    }
  };

  // Open delete confirmation dialog for tags
  const openDeleteTagDialog = (tagId, tagName) => {
    setDeleteTagDialog({ open: true, tagId, tagName });
  };

  // Close delete confirmation dialog
  const closeDeleteTagDialog = () => {
    setDeleteTagDialog({ open: false, tagId: null, tagName: '' });
  };

  // Handle confirmed tag deletion
  const handleConfirmDeleteTag = async () => {
    const { tagId } = deleteTagDialog;
    closeDeleteTagDialog();
    setDeletingTagId(tagId);
    
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags/${tagId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('Tag deleted permanently');
        fetchData();
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete tag');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete tag');
    } finally {
      setDeletingTagId(null);
    }
  };

  // Delete feedback handler
  const handleDeleteFeedback = async (feedbackId) => {
    setDeletingFeedbackId(feedbackId);
    setConfirmDeleteFeedbackId(null);
    
    try {
      const res = await nativeFetchWithAuth(`${API}/admin/feedback/${feedbackId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        setFeedbackList(feedbackList.filter(fb => fb.feedback_id !== feedbackId));
        toast.success('Feedback deleted');
      } else {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to delete feedback');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete feedback');
    } finally {
      setDeletingFeedbackId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-primary animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-panel">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="container-wide flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/dashboard')}
              className="text-zinc-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <LogoNav />
            <span className="text-primary font-medium">Admin</span>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="pt-24 pb-12 container-wide">
        <div className="mb-8">
          <div className="flex items-start justify-between gap-4 flex-wrap mb-2">
            <div>
              <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
              <p className="text-zinc-400">Manage your platform settings</p>
            </div>
            <LiveUsersWidget />
          </div>

          {/* Admin tools row — view-as-user switch lives here so it's
              accessible from every tab. */}
          <div className="mt-4 p-3 bg-zinc-900/60 border border-zinc-800 rounded-lg flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-2 h-2 rounded-full ${viewAsUser ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400'}`} />
              <div className="min-w-0">
                <p className="text-sm font-medium text-white">
                  {viewAsUser ? 'Viewing as regular user' : 'Full admin view'}
                </p>
                <p className="text-xs text-zinc-400 truncate">
                  {viewAsUser
                    ? 'Studio AI engines (Spicy, Avatar Lip-Sync, Char Sheets) are hidden from the AI Generate modal.'
                    : 'All AI engines visible. Toggle off to preview what regular users see.'}
                </p>
              </div>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none shrink-0" data-testid="admin-view-as-user-label">
              <span className="text-xs text-zinc-400">View as user</span>
              <button
                role="switch"
                aria-checked={viewAsUser}
                onClick={() => toggleViewAsUser(!viewAsUser)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  viewAsUser ? 'bg-amber-500' : 'bg-zinc-700'
                }`}
                data-testid="admin-view-as-user-toggle"
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    viewAsUser ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </label>
          </div>

          {/* Test Features Banner */}
          <div className="mt-4 p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg flex items-center justify-between">
            <div>
              <p className="text-purple-400 font-medium">🧪 Test New Feature: Public Library</p>
              <p className="text-zinc-400 text-sm">Browse and share songs with the community (testing phase)</p>
            </div>
            <button
              onClick={() => navigate('/library')}
              className="px-4 py-2 bg-purple-500 text-white font-medium rounded-lg hover:bg-purple-400 transition-colors"
            >
              Test Library
            </button>
            <button
              onClick={() => navigate('/slideshow-beta')}
              className="px-4 py-2 bg-blue-500 text-white font-medium rounded-lg hover:bg-blue-400 transition-colors"
            >
              Slideshow (Redesign)
            </button>
          </div>
        </div>

        {/* Tabs */}
        {(() => {
          // Regular admins see only the day-to-day tabs. Super admins see
          // everything, including destructive/infra tools like DB Migrate,
          // Utilities, and Client Errors. `superOnly: true` = super_admin only.
          const isSuperAdmin = user?.role === 'super_admin';
          const allTabs = [
            { id: 'stats', label: 'Statistics', icon: BarChart3 },
            { id: 'pricing', label: 'Pricing', icon: DollarSign, superOnly: true },
            { id: 'promos', label: 'Promo Codes', icon: Gift, superOnly: true },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'transactions', label: 'Transactions', icon: CreditCard, superOnly: true },
            { id: 'prompts', label: 'Prompts', icon: Tag },
            { id: 'songoptions', label: 'Song Options', icon: Music },
            { id: 'songhealth', label: 'Song Health', icon: HeartPulse, superOnly: true },
            { id: 'stylepresets', label: 'Style Presets', icon: Sparkles },
            { id: 'animation', label: 'Animation Studio', icon: Film },
            { id: 'coach', label: 'Coach', icon: Eye },
            { id: 'demofunnel', label: 'Demo Funnel', icon: TrendingUp, superOnly: true },
            { id: 'utilities', label: 'Utilities', icon: Settings, superOnly: true },
            { id: 'backups', label: 'DB Backups', icon: Database, superOnly: true },
            { id: 'dbmigrate', label: 'DB Migrate', icon: Database, superOnly: true },
            { id: 'feedback', label: 'Feedback', icon: MessageSquare },
            { id: 'errors', label: 'Client Errors', icon: Bug, superOnly: true },
            { id: 'birthdaynames', label: 'Birthday Names', icon: Cake },
            { id: 'restoresong', label: 'Restore Songs', icon: RefreshCw, href: '/admin/restore-song' },
          ];
          const visibleTabs = allTabs.filter((t) => isSuperAdmin || !t.superOnly);
          return (
            <div className="flex gap-2 mb-8 overflow-x-auto pb-2 admin-tabs-scroll">
              {visibleTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => (tab.href ? navigate(tab.href) : setActiveTab(tab.id))}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-primary text-white'
                      : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                  } ${tab.superOnly ? 'ring-1 ring-amber-500/30' : ''}`}
                  title={tab.superOnly ? 'Super admin only' : undefined}
                  data-testid={`tab-${tab.id}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                </button>
              ))}
            </div>
          );
        })()}

        {/* Stats Tab */}
        {activeTab === 'stats' && (
          <div className="space-y-6">
            {/* Stats cards - only show if stats loaded */}
            {stats && (
              <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stats-section">
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Users className="h-5 w-5 text-primary" />
                    <span className="text-zinc-400">Total Users</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{stats.total_users}</div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Music className="h-5 w-5 text-primary" />
                    <span className="text-zinc-400">Total Songs</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{stats.total_songs}</div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Image className="h-5 w-5 text-primary" />
                    <span className="text-zinc-400">Album Covers</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{stats.total_covers}</div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <Gift className="h-5 w-5 text-primary" />
                    <span className="text-zinc-400">Gift Cards</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{stats.total_gifts}</div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <CreditCard className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-400">Transactions</span>
                  </div>
                  <div className="text-3xl font-bold text-white">{stats.total_transactions}</div>
                </div>
                <div className="glass-card p-6">
                  <div className="flex items-center gap-3 mb-2">
                    <DollarSign className="h-5 w-5 text-green-500" />
                    <span className="text-zinc-400">Total Revenue</span>
                  </div>
                  <div className="text-3xl font-bold text-green-500">${stats.total_revenue}</div>
                </div>
              </div>

              {/* Image-Generation Cost Tracker (Atlas spend this month) */}
              {imageGenCost && (
                <div className="glass-card p-6 mt-6" data-testid="admin-image-gen-cost">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Image className="h-5 w-5 text-amber-400" />
                      Image Generation Spend
                    </h3>
                    <span className="text-[11px] text-zinc-500">
                      Since {new Date(imageGenCost.month_start).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">This Month</div>
                      <div
                        className="text-2xl font-bold text-amber-400 mt-1"
                        data-testid="image-gen-month-cost"
                      >
                        ${imageGenCost.month_cost_usd.toFixed(2)}
                      </div>
                      <div
                        className="text-xs text-zinc-400 mt-0.5"
                        data-testid="image-gen-month-count"
                      >
                        {imageGenCost.month_count} image{imageGenCost.month_count === 1 ? '' : 's'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">All Time</div>
                      <div className="text-2xl font-bold text-white mt-1">
                        ${imageGenCost.all_time_cost_usd.toFixed(2)}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {imageGenCost.all_time_count} total
                      </div>
                    </div>
                    <div className="col-span-2">
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">
                        By Provider (this month)
                      </div>
                      <div className="space-y-1">
                        {Object.keys(imageGenCost.by_provider_month).length === 0 ? (
                          <div className="text-xs text-zinc-500 italic">No generations yet this month</div>
                        ) : (
                          Object.entries(imageGenCost.by_provider_month).map(([prov, amt]) => (
                            <div key={prov} className="flex items-center justify-between bg-black/20 rounded px-2 py-1 text-xs">
                              <span className="text-zinc-300 capitalize">{prov}</span>
                              <span className="font-mono text-white">${amt.toFixed(4)}</span>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-3 italic">
                    Costs are estimated from Atlas&apos;s published per-image pricing. Emergent-routed
                    image edits are counted in the image total but show $0 (Emergent doesn&apos;t
                    expose per-request cost).
                  </div>
                </div>
              )}

              {/* MusicGPT Engine — in-flight, spend, and recent activity */}
              {musicgptStats && (
                <div className="glass-card p-6 mt-6" data-testid="admin-musicgpt-stats">
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                      <Zap className="h-5 w-5 text-emerald-400" />
                      MusicGPT Engine
                      {!musicgptStats.configured && (
                        <span className="text-[11px] font-normal bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded">
                          Not configured
                        </span>
                      )}
                    </h3>
                    <span
                      className={`text-xs font-mono px-2 py-0.5 rounded ${
                        musicgptStats.in_flight >= musicgptStats.max_concurrent
                          ? 'bg-red-500/20 text-red-300'
                          : musicgptStats.in_flight >= musicgptStats.max_concurrent * 0.8
                          ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-emerald-500/20 text-emerald-300'
                      }`}
                      data-testid="musicgpt-in-flight"
                    >
                      {musicgptStats.in_flight} / {musicgptStats.max_concurrent} in-flight
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">Total Songs</div>
                      <div className="text-2xl font-bold text-white mt-1" data-testid="musicgpt-total-songs">
                        {musicgptStats.totals.songs}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {musicgptStats.totals.completed} ok, {musicgptStats.totals.failed} failed
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">Songs Billed</div>
                      <div className="text-2xl font-bold text-emerald-400 mt-1" data-testid="musicgpt-credits-used">
                        ${(musicgptStats.totals.credits_actual || musicgptStats.totals.credits_estimated).toFixed(2)}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        {musicgptStats.totals.credits_actual ? 'actual USD' : 'estimated USD'}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">Total Spend</div>
                      <div className="text-2xl font-bold text-emerald-400 mt-1" data-testid="musicgpt-total-spend">
                        ${(musicgptStats.totals.usd_actual_all_in || musicgptStats.totals.usd_actual || musicgptStats.totals.usd_estimated).toFixed(2)}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">
                        songs + /prompt_to_lyrics
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-zinc-500">Avg / Song</div>
                      <div className="text-2xl font-bold text-white mt-1">
                        ${musicgptStats.totals.songs > 0
                          ? ((musicgptStats.totals.usd_actual_all_in || musicgptStats.totals.usd_actual || musicgptStats.totals.usd_estimated) / musicgptStats.totals.songs).toFixed(3)
                          : '0.000'}
                      </div>
                      <div className="text-xs text-zinc-400 mt-0.5">across all time</div>
                    </div>
                  </div>
                  {musicgptStats.totals.prompt_to_lyrics_calls > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/5 grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="musicgpt-prompt-to-lyrics-breakout">
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500">Pre-Lyrics Calls</div>
                        <div className="text-lg font-semibold text-white mt-1">
                          {musicgptStats.totals.prompt_to_lyrics_calls}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">/prompt_to_lyrics hits</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500">Pre-Lyrics Spend</div>
                        <div className="text-lg font-semibold text-amber-400 mt-1">
                          ${(musicgptStats.totals.prompt_to_lyrics_usd || 0).toFixed(4)}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">was previously untracked</div>
                      </div>
                      <div>
                        <div className="text-[11px] uppercase tracking-wider text-zinc-500">Avg / Pre-Lyrics Call</div>
                        <div className="text-lg font-semibold text-white mt-1">
                          ${musicgptStats.totals.prompt_to_lyrics_calls > 0
                            ? (musicgptStats.totals.prompt_to_lyrics_usd / musicgptStats.totals.prompt_to_lyrics_calls).toFixed(4)
                            : '0.0000'}
                        </div>
                        <div className="text-xs text-zinc-400 mt-0.5">
                          set MUSICGPT_PROMPT_TO_LYRICS_FLAT_COST_USD to override
                        </div>
                      </div>
                    </div>
                  )}
                  <div className="text-[11px] text-zinc-500 mt-3 italic">
                    Costs are USD, taken directly from MusicGPT&apos;s response fields (credit_estimate at submit,
                    conversion_cost final), plus a flat estimate for /prompt_to_lyrics pre-calls.
                    Confirmed against MusicGPT dashboard: ~$0.07/song at API Pro V7 tier (v3.0.128).
                  </div>

                  {/* v3.0.129: Song Profitability CSV export */}
                  <SongProfitabilityExport data-testid="song-profitability-export" />
                </div>
              )}

              {/* AI Engine Usage */}
              {aiEngineStats && aiEngineStats.engines && aiEngineStats.engines.length > 0 && (
                <div className="glass-card p-6 mt-6" data-testid="admin-ai-engine-stats">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Zap className="h-5 w-5 text-purple-400" />
                    AI Engine Usage
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Engine totals */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-300 mb-3">Jobs by Engine</h4>
                      <div className="space-y-2">
                        {aiEngineStats.engines.map(e => {
                          const successRate = e.total ? Math.round((e.completed / e.total) * 100) : 0;
                          return (
                            <div key={e.engine} className="flex items-center justify-between bg-black/20 rounded-lg px-3 py-2">
                              <span className="text-white capitalize font-medium">{e.engine}</span>
                              <div className="flex items-center gap-4 text-xs">
                                <span className="text-zinc-400">Total: <span className="text-white font-semibold">{e.total}</span></span>
                                <span className="text-emerald-400">✓ {e.completed}</span>
                                {e.failed > 0 && <span className="text-red-400">✗ {e.failed}</span>}
                                <span className="text-zinc-500">{successRate}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {/* Grok mode breakdown */}
                    <div>
                      <h4 className="text-sm font-medium text-zinc-300 mb-3">
                        Grok Mode Distribution
                        {aiEngineStats.grok_mode_total > 0 && (
                          <span className="text-zinc-500 font-normal ml-2">({aiEngineStats.grok_mode_total} jobs)</span>
                        )}
                      </h4>
                      {aiEngineStats.grok_modes && aiEngineStats.grok_modes.length > 0 ? (
                        <div className="space-y-2">
                          {aiEngineStats.grok_modes.map(m => (
                            <div key={m.mode} className="bg-black/20 rounded-lg px-3 py-2">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-white text-sm capitalize">
                                  {m.mode === 'startframe' ? 'Start Frame' : m.mode === 'combined' ? 'Start + Refs' : m.mode}
                                </span>
                                <span className="text-xs text-zinc-400">{m.count} · <span className="text-white font-semibold">{m.percent}%</span></span>
                              </div>
                              <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
                                  style={{ width: `${m.percent}%` }}
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-zinc-500 text-sm italic">No Grok jobs yet — data will appear as users generate videos.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Maintenance Actions */}
              <div className="glass-card p-6 mt-6">
              </div>
              </>
            )}
            
            {!stats && (
              <div className="glass-card p-6 text-center text-zinc-400">
                <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
                Loading statistics...
              </div>
            )}
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="grid lg:grid-cols-2 gap-8" data-testid="pricing-section">
            {/* Credit Packages */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Zap className="h-5 w-5 text-primary" />
                Credit Packages
              </h3>
              <div className="space-y-4">
                {packages.map((pkg, i) => (
                  <div key={pkg.package_id} className="bg-black/20 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Package ID</Label>
                        <Input
                          value={pkg.package_id}
                          onChange={(e) => updatePackage(i, 'package_id', e.target.value)}
                          className="input-dark mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Name</Label>
                        <Input
                          value={pkg.name}
                          onChange={(e) => updatePackage(i, 'name', e.target.value)}
                          className="input-dark mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Credits</Label>
                        <Input
                          type="number"
                          value={pkg.credits}
                          onChange={(e) => updatePackage(i, 'credits', e.target.value)}
                          className="input-dark mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Price ($)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={pkg.price}
                          onChange={(e) => updatePackage(i, 'price', e.target.value)}
                          className="input-dark mt-1"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <Button onClick={handleSavePackages} disabled={saving} className="btn-primary mt-4">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Packages
              </Button>
            </div>

            {/* Credit Costs — split into Core Actions and AI Engine Rates */}
            {/* Signup Bonus — pulled out of the generic Credit Costs
                grid because semantically it's a *grant* (given to every
                new member on registration), not a *cost per action*.
                Hides more discoverable than being row 10 in a list. */}
            <div className="glass-card p-6 border-emerald-500/30" data-testid="signup-bonus-card">
              <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-emerald-400" />
                Signup Bonus
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Free credits granted to every new user the moment they register
                (email or Google). Also applied when a demo visitor claims their
                demo on signup.
              </p>
              <div className="flex items-center justify-between gap-4" data-testid="cost-row-new_member_credits">
                <Label className="text-white text-base">New Member Credits</Label>
                <Input
                  type="number"
                  min="0"
                  value={costs.new_member_credits ?? 0}
                  onChange={(e) => updateCost('new_member_credits', e.target.value)}
                  className="input-dark w-32 text-base font-semibold"
                  data-testid="cost-input-new_member_credits"
                />
              </div>
              <Button onClick={handleSaveCosts} disabled={saving} className="btn-primary mt-4" data-testid="save-signup-bonus-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Signup Bonus
              </Button>
            </div>

            {/* Credit Costs — Signup bonus is intentionally excluded
                here since it now lives in its own card above. */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-primary" />
                Credit Costs per Action
              </h3>
              <div className="space-y-4">
                {Object.entries(costs)
                  .filter(([key]) => !key.startsWith('ai_') && key !== 'new_member_credits')
                  .map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between gap-4" data-testid={`cost-row-${key}`}>
                    <Label className="text-white capitalize">{key.replace(/_/g, ' ')}</Label>
                    <Input
                      type="number"
                      value={value}
                      onChange={(e) => updateCost(key, e.target.value)}
                      className="input-dark w-24"
                      data-testid={`cost-input-${key}`}
                    />
                  </div>
                ))}
              </div>
              <Button onClick={handleSaveCosts} disabled={saving} className="btn-primary mt-4" data-testid="save-costs-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Costs
              </Button>
            </div>

            {/* AI Engine Rates — per-second rates for AI video engines */}
            <div className="glass-card p-6 lg:col-span-2" data-testid="ai-engine-rates-card">
              <h3 className="text-lg font-bold text-white mb-1 flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-400" />
                AI Engine Rates
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Credits charged per second of generated video (per image for GPT Image 1). Users see a live
                credit cost when generating — these values are the source of truth.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[
                  { key: 'ai_seedance_per_second', label: 'Seedance 1.5', tier: 'Budget', unit: 'cr/s' },
                  { key: 'ai_wan_per_second',      label: 'Wan 2.6',       tier: 'Standard', unit: 'cr/s' },
                  { key: 'ai_vidu_per_second',     label: 'Vidu Q3 Pro',   tier: 'Standard', unit: 'cr/s' },
                  { key: 'ai_kling_per_second',    label: 'Kling 3.0',     tier: 'Quality', unit: 'cr/s' },
                  { key: 'ai_gpt_image',           label: 'GPT Image',     tier: 'Image',   unit: 'cr/img' },
                  // 🔒 ADMIN STUDIO engines — visible to admin role only in the customer modal
                  { key: 'ai_seedance_spicy_per_second', label: '🔒 Seedance Spicy',     tier: 'Admin', unit: 'cr/s' },
                  { key: 'ai_seedream_sequential',       label: '🔒 Character Sheets',   tier: 'Admin', unit: 'cr/call' },
                  // Sora 2 retired — hidden from customer UI. Backend value preserved
                  // in CREDIT_COSTS so any in-flight admin renders still cost correctly.
                  // Grok Imagine retired May 2026 — replaced by Vidu Q3 Pro (working start/end frame).
                  // Kling Avatar std + pro retired May 2026.
                ].map(engine => {
                  const value = costs[engine.key] ?? 0;
                  const preview10 = engine.unit === 'cr/s' ? value * 10 : value;
                  return (
                    <div key={engine.key} className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-4" data-testid={`ai-engine-${engine.key}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-white font-medium text-sm">{engine.label}</span>
                        <span className="text-xs text-zinc-500">{engine.tier}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <Input
                          type="number"
                          min="0"
                          value={value}
                          onChange={(e) => updateCost(engine.key, e.target.value)}
                          className="input-dark w-24"
                          data-testid={`ai-engine-input-${engine.key}`}
                        />
                        <span className="text-xs text-zinc-400">{engine.unit}</span>
                      </div>
                      {engine.unit === 'cr/s' && (
                        <p className="text-xs text-zinc-500 mt-2">
                          10s clip = <span className="text-white">{preview10} credits</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button onClick={handleSaveCosts} disabled={saving} className="btn-primary mt-4" data-testid="save-ai-costs-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save AI Rates
              </Button>
            </div>
          </div>
        )}

        {/* Promo Codes Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6" data-testid="promos-section">
            {/* Header */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Gift className="h-5 w-5 text-purple-400" />
                    Promo Gift Codes
                  </h3>
                  <p className="text-xs text-zinc-500 mt-1">
                    Generate batches of free-credit codes for influencers to give away to their audiences.
                    Default: 75 credits per code (one full creative loop), 6 months to expire.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setBatchResult(null);
                    setShowPromoBatchModal(true);
                  }}
                  className="btn-primary"
                  data-testid="generate-batch-btn"
                >
                  <Plus className="h-4 w-4 mr-2" /> Generate New Batch
                </Button>
              </div>
            </div>

            {/* This Week in Promo — 7-day digest card. Appears first
                so the highest-signal, most time-relevant info is what
                the admin sees when they open the tab. Same data will
                eventually power a Sunday-morning email once we wire up
                an email provider. */}
            {promoDigest && (
              (promoDigest.redemptions_last_7d.codes > 0 || promoDigest.expiring_next_7d.length > 0) ? (
                <div className="glass-card p-5 border border-primary/20" data-testid="promo-digest-card">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <h4 className="text-sm font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                      <span className="text-lg">📅</span> This Week in Promo
                    </h4>
                    <span className="text-xs text-zinc-500">
                      {new Date(promoDigest.window_start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                      {' – '}
                      {new Date(promoDigest.window_end).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {/* Column 1: 7-day redemption totals */}
                    <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="digest-redemptions">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Redeemed (7 days)</div>
                      <div className="text-2xl font-bold text-green-400">{promoDigest.redemptions_last_7d.codes}</div>
                      <div className="text-[11px] text-zinc-400 mt-1">
                        {promoDigest.redemptions_last_7d.credits.toLocaleString()} credits · ${promoDigest.redemptions_last_7d.hard_cost_usd.toFixed(2)} cost
                      </div>
                    </div>

                    {/* Column 2: top campaign of the week */}
                    <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="digest-top-campaign">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Top campaign this week</div>
                      {promoDigest.top_campaign ? (
                        <>
                          <div className="text-sm font-semibold text-white truncate">{promoDigest.top_campaign.campaign_name}</div>
                          <div className="text-[11px] text-zinc-400 mt-1 flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-300">
                              {promoDigest.top_campaign.campaign_tag}
                            </span>
                            <span className="text-green-400 font-semibold">{promoDigest.top_campaign.redeemed_count}</span>
                            <span>redemptions</span>
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-zinc-500 italic">No redemptions this week</div>
                      )}
                    </div>

                    {/* Column 3: campaigns expiring in the next 7 days */}
                    <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="digest-expiring">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Expiring in next 7 days</div>
                      {promoDigest.expiring_next_7d.length === 0 ? (
                        <div className="text-sm text-zinc-500 italic">Nothing expiring soon</div>
                      ) : (
                        <>
                          <div className="text-2xl font-bold text-orange-400">{promoDigest.expiring_next_7d.length}</div>
                          <div className="text-[11px] text-zinc-400 mt-1">
                            {promoDigest.expiring_next_7d.reduce((s, c) => s + c.unredeemed_count, 0)} unredeemed codes at risk
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Expiring list — only shown if any. Small so it doesn't
                      dominate the card. */}
                  {promoDigest.expiring_next_7d.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2">Expiring detail</div>
                      <div className="flex flex-col gap-1.5">
                        {promoDigest.expiring_next_7d.slice(0, 5).map((c) => (
                          <div key={c.campaign_id} className="flex items-center justify-between text-xs" data-testid={`digest-expiring-${c.campaign_id}`}>
                            <span className="text-zinc-300 truncate max-w-[60%]">
                              {c.campaign_name}
                              {c.influencer_handle && <span className="text-zinc-500 ml-2">· {c.influencer_handle}</span>}
                            </span>
                            <span className="text-orange-300 flex items-center gap-2 shrink-0">
                              <span>{c.unredeemed_count} unredeemed</span>
                              <span className="text-zinc-500">
                                {new Date(c.expires_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                              </span>
                            </span>
                          </div>
                        ))}
                        {promoDigest.expiring_next_7d.length > 5 && (
                          <div className="text-[11px] text-zinc-500 italic mt-1">
                            + {promoDigest.expiring_next_7d.length - 5} more
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                // Quiet-week state — one tiny line so the tab doesn't feel
                // dead but we don't waste real estate on an empty card.
                <div className="text-xs text-zinc-500 italic px-1" data-testid="promo-digest-quiet">
                  📅 Quiet week — 0 redemptions in the last 7 days, nothing expiring in the next 7. Enjoy the calm.
                </div>
              )
            )}

            {/* Program-wide summary — aggregated across ALL campaigns.
                Answers "how is the whole promo program doing?" without
                having to eyeball each card. */}
            {promoSummary && promoSummary.total_codes > 0 && (
              <div className="glass-card p-5" data-testid="promo-summary-card">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <h4 className="text-sm font-semibold text-white uppercase tracking-wide">
                    Promo Program Overview
                  </h4>
                  <span className="text-xs text-zinc-500">
                    Across {promoSummary.total_campaigns} campaign{promoSummary.total_campaigns === 1 ? '' : 's'}
                  </span>
                </div>

                {/* Redemption progress bar */}
                <div className="mb-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-zinc-400">
                      <span className="text-green-400 font-semibold">{promoSummary.codes_redeemed}</span>
                      <span className="text-zinc-500"> / {promoSummary.total_codes} codes redeemed</span>
                    </span>
                    <span className="text-white font-semibold">{promoSummary.redemption_rate_pct}%</span>
                  </div>
                  <div className="w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                      style={{ width: `${promoSummary.redemption_rate_pct}%` }}
                    />
                  </div>
                </div>

                {/* Key metrics grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="summary-available">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Available</div>
                    <div className="text-lg font-bold text-white">{promoSummary.codes_available}</div>
                    <div className="text-[11px] text-zinc-400">{promoSummary.credits_available.toLocaleString()} credits</div>
                  </div>
                  <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="summary-redeemed">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Redeemed</div>
                    <div className="text-lg font-bold text-green-400">{promoSummary.codes_redeemed}</div>
                    <div className="text-[11px] text-zinc-400">{promoSummary.credits_redeemed.toLocaleString()} credits</div>
                  </div>
                  <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="summary-realized-cost">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Realized cost</div>
                    <div className="text-lg font-bold text-amber-300">${promoSummary.hard_cost_realized_usd.toFixed(2)}</div>
                    <div className="text-[11px] text-zinc-400">Actual spend so far</div>
                  </div>
                  <div className="bg-black/20 border border-white/5 rounded-lg p-3" data-testid="summary-pending-cost">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-1">On the hook</div>
                    <div className="text-lg font-bold text-orange-400">${promoSummary.hard_cost_pending_usd.toFixed(2)}</div>
                    <div className="text-[11px] text-zinc-400">If all available redeem</div>
                  </div>
                </div>

                {(promoSummary.codes_expired > 0 || promoSummary.codes_revoked > 0) && (
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-500 flex-wrap">
                    {promoSummary.codes_expired > 0 && (
                      <span data-testid="summary-expired">Expired unused: <span className="text-zinc-300 font-semibold">{promoSummary.codes_expired}</span></span>
                    )}
                    {promoSummary.codes_revoked > 0 && (
                      <span data-testid="summary-revoked">Revoked: <span className="text-red-400 font-semibold">{promoSummary.codes_revoked}</span></span>
                    )}
                  </div>
                )}

                {/* 30-day redemption timeline. Shows the daily redeem
                    count across all campaigns; if all bars are zero the
                    chart still renders as an empty rail so admins can
                    tell at a glance the API is working and there just
                    hasn't been activity yet. */}
                {Array.isArray(promoSummary.timeline_30d) && promoSummary.timeline_30d.length > 0 && (
                  <div className="mt-5 pt-4 border-t border-white/5" data-testid="summary-timeline">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Redemptions — last 30 days</div>
                      {promoSummary.timeline_30d.every((d) => d.redeemed_count === 0) && (
                        <span className="text-[10px] text-zinc-600">No redemptions in this window</span>
                      )}
                    </div>
                    <ResponsiveContainer width="100%" height={80}>
                      <BarChart data={promoSummary.timeline_30d} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                        <XAxis
                          dataKey="date"
                          tick={{ fill: '#71717a', fontSize: 9 }}
                          tickFormatter={(d) => d ? d.slice(5) : ''}
                          interval={4}
                          axisLine={false}
                          tickLine={false}
                        />
                        <YAxis hide domain={[0, 'dataMax + 1']} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{ background: '#18181b', border: '1px solid #3f3f46', borderRadius: 6, fontSize: 11 }}
                          labelStyle={{ color: '#a1a1aa' }}
                          formatter={(v, name) => name === 'redeemed_count' ? [`${v} codes`, 'Redeemed'] : [`${v}`, name]}
                          labelFormatter={(l) => l}
                        />
                        <Bar dataKey="redeemed_count" fill="url(#redemptionGradient)" radius={[2, 2, 0, 0]} />
                        <defs>
                          <linearGradient id="redemptionGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#a855f7" />
                            <stop offset="100%" stopColor="#ec4899" />
                          </linearGradient>
                        </defs>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Sort / filter toolbar. Only shows once we have campaigns
                — no point cluttering the empty state. */}
            {promoCampaigns.length > 1 && (
              <div className="glass-card p-3 flex items-center gap-2 flex-wrap" data-testid="promo-toolbar">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="h-3.5 w-3.5 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={promoSearch}
                    onChange={(e) => setPromoSearch(e.target.value)}
                    placeholder="Search name, tag, or handle…"
                    className="w-full pl-8 pr-3 py-1.5 bg-black/30 border border-white/10 rounded text-white text-sm placeholder:text-zinc-600"
                    data-testid="promo-search-input"
                  />
                </div>
                <div className="flex gap-1" data-testid="promo-status-filter">
                  {[
                    { id: 'all', label: 'All' },
                    { id: 'active', label: 'Active' },
                    { id: 'expired', label: 'Expired' },
                    { id: 'depleted', label: 'Depleted' },
                  ].map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setPromoStatusFilter(f.id)}
                      className={`px-2.5 py-1.5 rounded text-xs font-medium transition-colors ${
                        promoStatusFilter === f.id
                          ? 'bg-primary/20 text-primary border border-primary/40'
                          : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                      }`}
                      data-testid={`promo-filter-${f.id}`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                <select
                  value={promoSort}
                  onChange={(e) => setPromoSort(e.target.value)}
                  className="px-2 py-1.5 bg-black/30 border border-white/10 rounded text-white text-xs"
                  data-testid="promo-sort-select"
                >
                  <option value="newest">Newest first</option>
                  <option value="oldest">Oldest first</option>
                  <option value="rate_desc">Highest redemption %</option>
                  <option value="rate_asc">Lowest redemption %</option>
                  <option value="codes_desc">Most codes</option>
                  <option value="expires_soon">Expires soon</option>
                </select>
              </div>
            )}

            {/* Campaigns list */}
            <div className="space-y-4">
              {promoLoading ? (
                <div className="flex justify-center p-12">
                  <Loader2 className="h-8 w-8 text-purple-400 animate-spin" />
                </div>
              ) : promoCampaigns.length === 0 ? (
                <div className="glass-card p-12 text-center">
                  <Gift className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-2">No promo campaigns yet</p>
                  <p className="text-zinc-600 text-sm">
                    Click "Generate New Batch" to create your first influencer campaign.
                  </p>
                </div>
              ) : (() => {
                // Derive the displayed list from the loaded campaigns +
                // toolbar state. Client-side is fine here — a promo tab
                // with 1000+ campaigns isn't realistic.
                const now = new Date().toISOString();
                const q = promoSearch.trim().toLowerCase();
                const filtered = promoCampaigns.filter((c) => {
                  if (q) {
                    const hay = `${c.campaign_name || ''} ${c.campaign_tag || ''} ${c.influencer_handle || ''}`.toLowerCase();
                    if (!hay.includes(q)) return false;
                  }
                  if (promoStatusFilter === 'active') {
                    if (c.expires_at && c.expires_at < now) return false;
                    if (c.redeemed_count + (c.revoked_count || 0) >= c.total_codes) return false;
                  } else if (promoStatusFilter === 'expired') {
                    if (!c.expires_at || c.expires_at >= now) return false;
                  } else if (promoStatusFilter === 'depleted') {
                    if (c.redeemed_count + (c.revoked_count || 0) < c.total_codes) return false;
                  }
                  return true;
                });
                const sorted = [...filtered].sort((a, b) => {
                  switch (promoSort) {
                    case 'oldest': return (a.created_at || '').localeCompare(b.created_at || '');
                    case 'rate_desc': return (b.redemption_rate || 0) - (a.redemption_rate || 0);
                    case 'rate_asc': return (a.redemption_rate || 0) - (b.redemption_rate || 0);
                    case 'codes_desc': return (b.total_codes || 0) - (a.total_codes || 0);
                    case 'expires_soon':
                      // Nulls (no expiry) sort to the end.
                      if (!a.expires_at && !b.expires_at) return 0;
                      if (!a.expires_at) return 1;
                      if (!b.expires_at) return -1;
                      return a.expires_at.localeCompare(b.expires_at);
                    case 'newest':
                    default:
                      return (b.created_at || '').localeCompare(a.created_at || '');
                  }
                });
                if (sorted.length === 0) {
                  return (
                    <div className="glass-card p-8 text-center text-zinc-500 text-sm" data-testid="promo-empty-filtered">
                      No campaigns match the current filter. Try clearing the search or switching filters.
                    </div>
                  );
                }
                return sorted.map((c) => {
                  const isOpen = promoExpanded === c.campaign_id;
                  const detail = isOpen ? promoCampaignDetail : null;
                  return (
                    <div key={c.campaign_id} className="glass-card p-5" data-testid={`campaign-${c.campaign_id}`}>
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-[250px]">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-white font-semibold text-base">{c.campaign_name}</h4>
                            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30">
                              {c.campaign_tag}
                            </span>
                            {c.influencer_handle && (
                              <span className="text-xs text-zinc-500">• {c.influencer_handle}</span>
                            )}
                          </div>
                          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-400 flex-wrap">
                            <span>
                              <span className="text-white font-semibold">{c.credits_per_code}</span> cr × <span className="text-white font-semibold">{c.total_codes}</span> codes
                            </span>
                            <span>
                              Redeemed: <span className="text-green-400 font-semibold">{c.redeemed_count}/{c.total_codes}</span>
                              <span className="text-zinc-500"> ({c.redemption_rate}%)</span>
                            </span>
                            {c.revoked_count > 0 && (
                              <span className="text-red-400">Revoked: {c.revoked_count}</span>
                            )}
                            <span>
                              Expires: {new Date(c.expires_at).toLocaleDateString()}
                            </span>
                          </div>
                          {/* Redemption bar */}
                          <div className="mt-3 w-full h-2 bg-zinc-800 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all"
                              style={{ width: `${c.redemption_rate}%` }}
                            />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => isOpen ? setPromoExpanded(null) : loadPromoCampaignDetail(c.campaign_id)}
                            data-testid={`campaign-toggle-${c.campaign_id}`}
                          >
                            {isOpen ? 'Hide' : 'View Codes'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDownloadCsv(c.campaign_id, c.campaign_name)}
                            data-testid={`campaign-csv-${c.campaign_id}`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1" /> CSV
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`/admin/promo/print/${c.campaign_id}`, '_blank', 'noopener')}
                            data-testid={`campaign-print-${c.campaign_id}`}
                            title="Open printable report in a new tab"
                          >
                            <FileText className="h-3.5 w-3.5 mr-1" /> Print
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePauseCampaign(c.campaign_id, c.campaign_name)}
                            className="text-red-400 hover:text-red-300"
                            data-testid={`campaign-pause-${c.campaign_id}`}
                          >
                            <Pause className="h-3.5 w-3.5 mr-1" /> Pause
                          </Button>
                        </div>
                      </div>

                      {/* Expanded detail */}
                      {isOpen && detail && (
                        <div className="mt-5 pt-5 border-t border-zinc-800 space-y-3">
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <span className="text-sm text-zinc-400">
                              All {detail.codes.length} codes in this campaign:
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCopyAllCodes(detail.codes)}
                              data-testid={`copy-all-${c.campaign_id}`}
                            >
                              Copy All Codes
                            </Button>
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-96 overflow-y-auto">
                            {detail.codes.map((code) => (
                              <div
                                key={code.code}
                                className={`font-mono text-sm px-3 py-2 rounded border flex items-center justify-between ${
                                  code.revoked
                                    ? 'bg-red-900/20 border-red-800/40 text-red-400 line-through'
                                    : code.redeemed
                                    ? 'bg-green-900/20 border-green-800/40 text-green-400'
                                    : 'bg-zinc-800/50 border-zinc-700 text-zinc-300'
                                }`}
                              >
                                <span>{code.code}</span>
                                {code.revoked ? (
                                  <span className="text-[10px] uppercase">Revoked</span>
                                ) : code.redeemed ? (
                                  <CheckCircle className="h-3.5 w-3.5" />
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                });
              })()}
            </div>

            {/* Generate Batch Dialog */}
            <Dialog open={showPromoBatchModal} onOpenChange={setShowPromoBatchModal}>
              <DialogContent className="max-w-lg" data-testid="promo-batch-modal">
                <DialogHeader>
                  <DialogTitle className="text-white">Generate Promo Code Batch</DialogTitle>
                  <DialogDescription className="text-zinc-400">
                    Create a batch of codes for one influencer campaign. All codes share the same tag, credit amount, and expiration.
                  </DialogDescription>
                </DialogHeader>

                {!batchResult ? (
                  <div className="space-y-4 py-2">
                    <div>
                      <Label className="text-zinc-300 text-sm">Campaign Name</Label>
                      <Input
                        value={newBatch.campaign_name}
                        onChange={(e) => setNewBatch({ ...newBatch, campaign_name: e.target.value })}
                        placeholder="TikTok @musicmaker123"
                        className="input-dark"
                        data-testid="promo-campaign-name"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-300 text-sm">Campaign Tag</Label>
                        <Input
                          value={newBatch.campaign_tag}
                          onChange={(e) => setNewBatch({ ...newBatch, campaign_tag: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10) })}
                          placeholder="TIKTOK"
                          className="input-dark uppercase font-mono"
                          data-testid="promo-campaign-tag"
                        />
                        <p className="text-xs text-zinc-500 mt-1">Appears in codes: WIM-{newBatch.campaign_tag || 'TIKTOK'}-XXXX</p>
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-sm">Influencer Handle (optional)</Label>
                        <Input
                          value={newBatch.influencer_handle}
                          onChange={(e) => setNewBatch({ ...newBatch, influencer_handle: e.target.value })}
                          placeholder="@musicmaker123"
                          className="input-dark"
                          data-testid="promo-influencer-handle"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-zinc-300 text-sm">Credits per Code</Label>
                        <Input
                          type="number"
                          min="1"
                          max="10000"
                          value={newBatch.credits}
                          onChange={(e) => setNewBatch({ ...newBatch, credits: parseInt(e.target.value) || 0 })}
                          className="input-dark"
                          data-testid="promo-credits"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-sm">Number of Codes</Label>
                        <Input
                          type="number"
                          min="1"
                          max="1000"
                          value={newBatch.count}
                          onChange={(e) => setNewBatch({ ...newBatch, count: parseInt(e.target.value) || 0 })}
                          className="input-dark"
                          data-testid="promo-count"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300 text-sm">Expires in (days)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={newBatch.expires_in_days}
                          onChange={(e) => setNewBatch({ ...newBatch, expires_in_days: parseInt(e.target.value) || 0 })}
                          className="input-dark"
                          data-testid="promo-expires"
                        />
                      </div>
                    </div>

                    <div className="bg-purple-900/20 border border-purple-700/40 rounded-lg p-3">
                      <p className="text-sm text-purple-300">
                        <span className="font-semibold">Total to mint:</span>{' '}
                        {(newBatch.count || 0).toLocaleString()} codes ·{' '}
                        {((newBatch.count || 0) * (newBatch.credits || 0)).toLocaleString()} credits at stake
                      </p>
                      <p className="text-xs text-purple-200/70 mt-1">
                        Estimated hard cost: ~${(((newBatch.count || 0) * (newBatch.credits || 0)) * 0.003).toFixed(2)} if all are redeemed
                      </p>
                    </div>
                  </div>
                ) : (
                  /* Batch result view */
                  <div className="space-y-3 py-2" data-testid="batch-result">
                    <div className="bg-green-900/20 border border-green-700/40 rounded-lg p-4 text-center">
                      <CheckCircle className="h-10 w-10 text-green-400 mx-auto mb-2" />
                      <p className="text-white font-semibold">Generated {batchResult.count} codes</p>
                      <p className="text-xs text-zinc-400 mt-1">
                        Campaign: {batchResult.campaign_name} · Tag: <span className="font-mono">{batchResult.campaign_tag}</span>
                      </p>
                    </div>
                    <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3 max-h-64 overflow-y-auto font-mono text-sm space-y-1">
                      {(batchResult.codes || []).map((code) => (
                        <div key={code} className="text-zinc-300">{code}</div>
                      ))}
                    </div>
                    <Button
                      onClick={() => handleCopyAllCodes((batchResult.codes || []).map(c => ({ code: c })))}
                      variant="outline"
                      className="w-full"
                      data-testid="batch-result-copy"
                    >
                      Copy All {batchResult.count} Codes
                    </Button>
                  </div>
                )}

                <DialogFooter>
                  {!batchResult ? (
                    <>
                      <Button variant="outline" onClick={() => setShowPromoBatchModal(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleGenerateBatch} disabled={creatingBatch} className="btn-primary" data-testid="promo-batch-submit">
                        {creatingBatch ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-2" /> Generate Batch</>}
                      </Button>
                    </>
                  ) : (
                    <Button onClick={() => { setShowPromoBatchModal(false); setBatchResult(null); }} className="btn-primary">
                      Done
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (() => {
          // Cycle a column through: none → asc → desc → none
          const cycleSort = (key) => {
            setUserSort(prev => {
              if (prev.key !== key) return { key, dir: 'asc' };
              if (prev.dir === 'asc') return { key, dir: 'desc' };
              return { key: null, dir: null };
            });
          };
          const SortIcon = ({ col }) => {
            if (userSort.key !== col) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
            return userSort.dir === 'asc'
              ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
              : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
          };
          // Apply search + sort client-side over the server-supplied list.
          const lowerSearch = userSearch.trim().toLowerCase();
          let displayedUsers = !lowerSearch
            ? users
            : users.filter(u =>
                (u.name || '').toLowerCase().includes(lowerSearch) ||
                (u.email || '').toLowerCase().includes(lowerSearch)
              );
          if (userSort.key) {
            // Helper: extract a normalized first name for sort. Empty/null
            // names get sentinel "~" so they fall to the END (after Z) in
            // ascending order, matching user expectation.
            const firstName = (u) => {
              const raw = (u.name || '').trim();
              if (!raw) return '~~~';
              return raw.split(/\s+/)[0].toLowerCase();
            };
            const fullName = (u) => {
              const raw = (u.name || '').trim();
              return raw ? raw.toLowerCase() : '~~~';
            };
            const keyMap = {
              // User column: primarily by FIRST NAME (what the eye scans),
              // tiebreak by full name + email so the order is stable.
              user: u => `${firstName(u)}\t${fullName(u)}\t${(u.email || '').toLowerCase()}`,
              email: u => (u.email || '~~~').toLowerCase(),
              role: u => (u.role || 'user').toLowerCase(),
              credits: u => Number(u.credits ?? 0),
              joined: u => u.created_at || '',
            };
            const fn = keyMap[userSort.key];
            // Use localeCompare for proper Unicode/diacritic handling
            // (Ștefan sorts near Stefan instead of after Z, etc.).
            const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
            displayedUsers = [...displayedUsers].sort((a, b) => {
              const av = fn(a); const bv = fn(b);
              let cmp;
              if (typeof av === 'number' && typeof bv === 'number') {
                cmp = av - bv;
              } else {
                cmp = collator.compare(String(av), String(bv));
              }
              return userSort.dir === 'asc' ? cmp : -cmp;
            });
          }
          // Header button — keyboard accessible, shows the right arrow icon.
          const HeaderBtn = ({ col, children, testid }) => (
            <button
              type="button"
              data-testid={testid}
              onClick={() => cycleSort(col)}
              className="inline-flex items-center gap-1.5 text-zinc-400 font-medium hover:text-white transition-colors cursor-pointer select-none focus:outline-none focus:text-white"
            >
              <span>{children}</span>
              <SortIcon col={col} />
            </button>
          );

          return (
          <div className="glass-card p-6" data-testid="users-section">
            {/* Search Bar — sort is now driven by clickable column headers */}
            <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-zinc-500" />
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    data-testid="admin-users-search-input"
                    className="bg-zinc-800 border border-zinc-700 rounded-lg pl-10 pr-4 py-2 text-white w-64 focus:outline-none focus:border-purple-500"
                  />
                </div>
              </div>
              <div className="text-zinc-500 text-xs">
                Tip: click a column header to sort. Click again to reverse, a third time to clear.
              </div>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4">
                      <HeaderBtn col="user" testid="admin-users-sort-user">User</HeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <HeaderBtn col="email" testid="admin-users-sort-email">Email</HeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <HeaderBtn col="role" testid="admin-users-sort-role">Role</HeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <HeaderBtn col="credits" testid="admin-users-sort-credits">Credits</HeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <HeaderBtn col="joined" testid="admin-users-sort-joined">Joined</HeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4 text-zinc-400 font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedUsers.map((u) => (
                    <tr key={u.user_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-8 h-8 rounded-full" />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                              <Users className="h-4 w-4 text-primary" />
                            </div>
                          )}
                          <span className="text-white">{u.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-zinc-400">{u.email}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.role === 'super_admin'
                            ? 'bg-amber-500/20 text-amber-300'
                            : u.role === 'admin'
                              ? 'bg-primary/20 text-primary'
                              : 'bg-zinc-700 text-zinc-300'
                        }`}>
                          {u.role === 'super_admin' ? '⭐ Super' : u.role === 'admin' ? '👑 Admin' : 'User'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="credit-badge">
                          <Zap className="h-3 w-3" />
                          {u.credits}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-zinc-400">
                        {new Date(u.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex gap-2 flex-wrap">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleEditCredits(u)}
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                            data-testid={`edit-credits-${u.user_id}`}
                          >
                            <Edit2 className="h-3 w-3 mr-1" />
                            Credits
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleViewUserSongs(u)}
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                            data-testid={`view-songs-${u.user_id}`}
                          >
                            <Music className="h-3 w-3 mr-1" />
                            Songs
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openImportSongModal(u)}
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                            data-testid={`import-song-${u.user_id}`}
                            title="Import an MP3 into this user's library"
                          >
                            <Upload className="h-3 w-3 mr-1" />
                            Import
                          </Button>
                          {user?.role === 'super_admin' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleToggleRole(u)}
                              className={`border-white/10 ${
                                u.role === 'admin' || u.role === 'super_admin'
                                  ? 'bg-primary/10 hover:bg-primary/20 text-primary'
                                  : 'bg-white/5 hover:bg-white/10 text-white'
                              }`}
                              data-testid={`toggle-role-${u.user_id}`}
                            >
                              {u.role === 'admin' || u.role === 'super_admin' ? 'Remove Admin' : 'Make Admin'}
                            </Button>
                          )}
                          {user?.role === 'super_admin' && u.user_id !== user?.user_id && (
                            <select
                              value={u.role || 'user'}
                              onChange={(e) => handleChangeRole(u, e.target.value)}
                              className="text-xs bg-amber-500/10 border border-amber-500/30 text-amber-300 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-amber-400"
                              title="Change role (super admin only)"
                              data-testid={`role-select-${u.user_id}`}
                            >
                              <option value="user">user</option>
                              <option value="admin">admin</option>
                              <option value="super_admin">super_admin</option>
                            </select>
                          )}
                          {u.role !== 'admin' && u.role !== 'super_admin' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => setDeleteUserDialog({ open: true, user: u })}
                              className="bg-red-500/10 border-red-500/30 hover:bg-red-500/20 text-red-400"
                              data-testid={`delete-user-${u.user_id}`}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {displayedUsers.length === 0 && (
              <div className="text-center py-12 text-zinc-400">No users found</div>
            )}
            {displayedUsers.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10 text-sm text-zinc-400">
                Showing {displayedUsers.length}
                {displayedUsers.length !== users.length ? ` of ${users.length}` : ''} users
                {userSort.key && (
                  <span className="ml-2 text-zinc-500">
                    · sorted by {userSort.key} ({userSort.dir})
                  </span>
                )}
              </div>
            )}
          </div>
          );
        })()}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (() => {
          // Click-to-sort cycle: none → asc → desc → none (same as Users tab).
          const cycleTxnSort = (key) => {
            setTxnSort(prev => {
              if (prev.key !== key) return { key, dir: 'asc' };
              if (prev.dir === 'asc') return { key, dir: 'desc' };
              return { key: null, dir: null };
            });
          };
          const TxnSortIcon = ({ col }) => {
            if (txnSort.key !== col) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
            return txnSort.dir === 'asc'
              ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
              : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
          };
          const TxnHeaderBtn = ({ col, children, testid }) => (
            <button
              type="button"
              data-testid={testid}
              onClick={() => cycleTxnSort(col)}
              className="inline-flex items-center gap-1.5 text-zinc-400 font-medium hover:text-white transition-colors cursor-pointer select-none focus:outline-none focus:text-white"
            >
              <span>{children}</span>
              <TxnSortIcon col={col} />
            </button>
          );
          // Apply sort client-side (date filtering still hits the server).
          let displayedTxns = transactions;
          if (txnSort.key) {
            const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
            const keyMap = {
              txn: t => Number(t.txn_number ?? 0),
              date: t => t.created_at || '',
              user: t => `${(t.user_name || '~~~').toLowerCase()}\t${(t.user_email || '').toLowerCase()}`,
              package: t => (t.metadata?.package_name || '~~~').toLowerCase(),
              amount: t => Number(t.amount ?? 0),
              credits: t => Number(t.credits ?? 0),
              status: t => (t.payment_status || '').toLowerCase(),
            };
            const fn = keyMap[txnSort.key];
            displayedTxns = [...displayedTxns].sort((a, b) => {
              const av = fn(a); const bv = fn(b);
              let cmp;
              if (typeof av === 'number' && typeof bv === 'number') {
                cmp = av - bv;
              } else {
                cmp = collator.compare(String(av), String(bv));
              }
              return txnSort.dir === 'asc' ? cmp : -cmp;
            });
          }

          return (
          <div className="glass-card p-6" data-testid="transactions-section">
            {/* Header with Filters and Print Button */}
            <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
              <h3 className="text-lg font-semibold text-white">Payment Transactions</h3>
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <label className="text-zinc-400 text-sm">From:</label>
                  <input
                    type="date"
                    id="txn-start-date"
                    className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm"
                    onChange={(e) => {
                      const startDate = e.target.value;
                      const endDate = document.getElementById('txn-end-date')?.value || '';
                      let url = `${API}/admin/transactions?limit=500`;
                      if (startDate) url += `&start_date=${startDate}`;
                      if (endDate) url += `&end_date=${endDate}`;
                      fetch(url, { credentials: 'include' })
                        .then(res => res.json())
                        .then(data => setTransactions(data))
                        .catch(err => console.error(err));
                    }}
                  />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-zinc-400 text-sm">To:</label>
                  <input
                    type="date"
                    id="txn-end-date"
                    className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-sm"
                    onChange={(e) => {
                      const endDate = e.target.value;
                      const startDate = document.getElementById('txn-start-date')?.value || '';
                      let url = `${API}/admin/transactions?limit=500`;
                      if (startDate) url += `&start_date=${startDate}`;
                      if (endDate) url += `&end_date=${endDate}`;
                      fetch(url, { credentials: 'include' })
                        .then(res => res.json())
                        .then(data => setTransactions(data))
                        .catch(err => console.error(err));
                    }}
                  />
                </div>
                <button
                  onClick={() => {
                    // Escape HTML to prevent XSS via user-supplied transaction fields
                    const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
                      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
                    }[c]));
                    const startDate = esc(document.getElementById('txn-start-date')?.value || 'All time');
                    const endDate = esc(document.getElementById('txn-end-date')?.value || 'Present');
                    const totalPaid = transactions
                      .filter(t => t.payment_status === 'paid')
                      .reduce((sum, t) => sum + (Number(t.amount) || 0), 0)
                      .toFixed(2);
                    const printContent = `
                      <!DOCTYPE html>
                      <html>
                      <head>
                        <title>Transaction Report - Wrapped In Music</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1 { text-align: center; margin-bottom: 10px; }
                          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
                          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; font-size: 11px; }
                          th { background-color: #4a5568; color: white; }
                          tr:nth-child(even) { background-color: #f9f9f9; }
                          .paid { color: green; font-weight: bold; }
                          .pending { color: orange; }
                          .header { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 12px; }
                          .total { margin-top: 20px; font-weight: bold; font-size: 14px; }
                          .txn-num { font-weight: bold; }
                          @media print { body { padding: 0; } }
                        </style>
                      </head>
                      <body>
                        <h1>Wrapped In Music</h1>
                        <p class="subtitle">Transaction Report: ${startDate} to ${endDate}</p>
                        <div class="header">
                          <span>Generated: ${esc(new Date().toLocaleString())}</span>
                          <span>Total Transactions: ${transactions.length}</span>
                        </div>
                        <table>
                          <thead>
                            <tr>
                              <th>TXN #</th>
                              <th>Date</th>
                              <th>User</th>
                              <th>Email</th>
                              <th>Package</th>
                              <th>Amount</th>
                              <th>Credits</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${transactions.map(txn => `
                              <tr>
                                <td class="txn-num">${esc(txn.txn_number ? 'TXN-' + String(txn.txn_number).padStart(7, '0') : '-')}</td>
                                <td>${esc(new Date(txn.created_at).toLocaleDateString())}</td>
                                <td>${esc(txn.user_name || 'Unknown')}</td>
                                <td>${esc(txn.user_email || 'Unknown')}</td>
                                <td>${esc(txn.metadata?.package_name || '-')}</td>
                                <td>$${esc(txn.amount)}</td>
                                <td>${esc(txn.credits)}</td>
                                <td class="${txn.payment_status === 'paid' ? 'paid' : 'pending'}">${esc(txn.payment_status)}</td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                        <div class="total">
                          Total Revenue (Paid): $${totalPaid}
                        </div>
                      </body>
                      </html>
                    `;
                    // Use Blob URL instead of the old DOM-write API to avoid the XSS warning
                    const blob = new Blob([printContent], { type: 'text/html' });
                    const url = URL.createObjectURL(blob);
                    const printWindow = window.open(url, '_blank');
                    if (printWindow) {
                      printWindow.addEventListener('load', () => {
                        try { printWindow.print(); } catch (err) { console.warn('Print failed:', err); }
                        setTimeout(() => URL.revokeObjectURL(url), 2000);
                      });
                    } else {
                      URL.revokeObjectURL(url);
                      console.warn('Print window blocked by browser popup blocker');
                    }
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
                >
                  <FileText className="h-4 w-4" />
                  Print Report
                </button>
              </div>
            </div>
            
            {/* Transactions Table */}
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="txn" testid="admin-txn-sort-txn">TXN #</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="date" testid="admin-txn-sort-date">Date</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="user" testid="admin-txn-sort-user">User</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="package" testid="admin-txn-sort-package">Package</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="amount" testid="admin-txn-sort-amount">Amount</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="credits" testid="admin-txn-sort-credits">Credits</TxnHeaderBtn>
                    </th>
                    <th className="text-left py-3 px-4">
                      <TxnHeaderBtn col="status" testid="admin-txn-sort-status">Status</TxnHeaderBtn>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {displayedTxns.map((txn) => (
                    <tr key={txn.transaction_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white font-bold">
                        {txn.txn_number ? `TXN-${String(txn.txn_number).padStart(7, '0')}` : '-'}
                      </td>
                      <td className="py-3 px-4 text-zinc-400">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="text-white">{txn.user_name || 'Unknown'}</div>
                        <div className="text-xs text-zinc-500">{txn.user_email || ''}</div>
                      </td>
                      <td className="py-3 px-4 text-zinc-300">
                        {txn.metadata?.package_name || '-'}
                      </td>
                      <td className="py-3 px-4 text-green-500 font-semibold">${txn.amount}</td>
                      <td className="py-3 px-4">
                        <span className="credit-badge">
                          <Zap className="h-3 w-3" />
                          {txn.credits}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          txn.payment_status === 'paid' ? 'status-completed' : 'status-pending'
                        }`}>
                          {txn.payment_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {displayedTxns.length === 0 && (
              <div className="text-center py-12 text-zinc-400">No transactions found for selected date range</div>
            )}
            {displayedTxns.length > 0 && (
              <div className="mt-4 pt-4 border-t border-white/10 flex justify-between text-sm">
                <span className="text-zinc-400">
                  Showing {displayedTxns.length} transactions
                  {txnSort.key && (
                    <span className="ml-2 text-zinc-500">
                      · sorted by {txnSort.key} ({txnSort.dir})
                    </span>
                  )}
                </span>
                <span className="text-green-500 font-semibold">
                  Total Revenue (Paid): ${displayedTxns.filter(t => t.payment_status === 'paid').reduce((sum, t) => sum + (t.amount || 0), 0).toFixed(2)}
                </span>
              </div>
            )}
          </div>
          );
        })()}

        {/* Prompts Tab */}
        {activeTab === 'prompts' && (
          <div className="space-y-6" data-testid="prompts-section">
            {/* Stats Bar */}
            <div className="glass-card p-4 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{promptTags.length}</p>
                  <p className="text-xs text-zinc-400">Total Tags</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-primary">{Object.keys(promptCategories).length + 1}</p>
                  <p className="text-xs text-zinc-400">Categories</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-emerald-500/10 border-emerald-500/30 text-emerald-300 hover:bg-emerald-500/20 hover:text-emerald-200"
                  onClick={async () => {
                    try {
                      const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags/export`, {
                        credentials: 'include',
                      });
                      if (!res.ok) throw new Error(`HTTP ${res.status}`);
                      const data = await res.json();
                      const blob = new Blob(
                        [JSON.stringify(data, null, 2)],
                        { type: 'application/json' }
                      );
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `prompt_tags_backup_${new Date().toISOString().slice(0,10)}.json`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                      toast.success(`Exported ${data.count} prompts`);
                    } catch (e) {
                      console.error('Export failed', e);
                      toast.error('Export failed — check console');
                    }
                  }}
                  data-testid="prompts-export-btn"
                  title="Download a JSON backup of every prompt tag"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Export
                </Button>
                <Input
                  placeholder="Search tags..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input-dark w-48"
                />
              </div>
            </div>

            {/* Category Tabs */}
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['structure', 'artists', 'vocals', 'instruments', 'mood', 'genres', 'effects'].map(cat => {
                const count = promptTags.filter(t => t.category === cat).length;
                const icons = {
                  structure: '🏗️',
                  vocals: '🎤',
                  instruments: '🎹',
                  mood: '💫',
                  genres: '🎵',
                  effects: '🔊',
                  artists: '🎨'
                };
                const displayNames = {
                  structure: 'Structure',
                  vocals: 'Vocals',
                  instruments: 'Instruments',
                  mood: 'Mood',
                  genres: 'Genres',
                  effects: 'Effects',
                  artists: 'Presets'
                };
                return (
                  <button
                    key={cat}
                    onClick={() => setActivePromptCategory(cat)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                      activePromptCategory === cat
                        ? 'bg-primary text-black'
                        : 'bg-white/5 text-zinc-400 hover:bg-white/10'
                    }`}
                  >
                    {icons[cat]} {displayNames[cat]} ({count})
                  </button>
                );
              })}
            </div>

            {/* Presets Bulk Import - Only show when artists tab is active */}
            {activePromptCategory === 'artists' && (
              <div className="glass-card p-6 border border-purple-500/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-purple-400" />
                  Bulk Import Style Presets
                </h3>
                <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 mb-4">
                  <p className="text-purple-200 text-sm">
                    🎨 Style presets approximate the sound style and instrumentation. Vocals are AI-generated and will not replicate any original artist's voice.
                  </p>
                </div>
                
                <div className="mb-4">
                  <Label className="text-zinc-400 text-xs">Subcategory (optional - applies to all presets below)</Label>
                  <Input
                    id="presetSubcategory"
                    placeholder="e.g., '70s Wonder Tunes, '80s Synth Pop, Modern Hip-Hop"
                    className="input-dark mt-1"
                  />
                </div>
                
                <p className="text-zinc-400 text-sm mb-2">
                  Paste preset data in format: <code className="bg-white/10 px-2 py-0.5 rounded text-purple-300">Preset Name: style1, style2, style3</code>
                </p>
                <textarea
                  id="artistImportData"
                  className="w-full h-48 bg-black/30 border border-white/10 rounded-lg p-4 text-white text-sm font-mono resize-none focus:border-purple-500 focus:outline-none"
                  placeholder={`Led Zeppelin: Hard rock, blues influence, heavy riffs, male vocals, epic energy
Pink Floyd: Psychedelic rock, atmospheric synths, slow builds, abstract mood
Queen: Glam rock, dramatic vocals, multi-part harmonies, theatrical energy`}
                />
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={async () => {
                      const textarea = document.getElementById('artistImportData');
                      const subcategoryInput = document.getElementById('presetSubcategory');
                      const data = textarea.value.trim();
                      const subcategory = subcategoryInput.value.trim();
                      if (!data) {
                        toast.error('Please paste preset data first');
                        return;
                      }
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags/import-artists`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ data, subcategory: subcategory || null })
                        });
                        const result = await res.json();
                        if (res.ok) {
                          toast.success(`Imported ${result.imported} presets! ${result.skipped > 0 ? `(${result.skipped} skipped)` : ''}`);
                          textarea.value = '';
                          // Refresh tags
                          const tagsRes = await nativeFetchWithAuth(`${API}/prompt-tags`, { credentials: 'include' });
                          const tagsData = await tagsRes.json();
                          setPromptTags(tagsData);
                        } else {
                          toast.error(result.detail || 'Import failed');
                        }
                      } catch (err) {
                        toast.error('Import failed: ' + err.message);
                      }
                    }}
                    className="bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import Presets
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      document.getElementById('artistImportData').value = '';
                      document.getElementById('presetSubcategory').value = '';
                    }}
                    className="border-white/20 text-zinc-400 hover:text-white"
                  >
                    Clear
                  </Button>
                </div>
                {promptTags.filter(t => t.category === 'artists').length > 0 && (
                  <p className="text-zinc-500 text-xs mt-3">
                    ✓ {promptTags.filter(t => t.category === 'artists').length} presets currently in database
                  </p>
                )}
              </div>
            )}

            {/* Bulk Import for Other Categories (not artists) */}
            {activePromptCategory !== 'artists' && (
              <div className="glass-card p-6 border border-primary/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Upload className="h-5 w-5 text-primary" />
                  Bulk Import Tags to "{activePromptCategory}"
                </h3>
                <p className="text-zinc-400 text-sm mb-4">
                  Import multiple tags at once. Format: <code className="bg-white/10 px-2 py-0.5 rounded text-primary">Tag Name: description, use case</code>
                </p>
                
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">Category</Label>
                    <Input
                      value={activePromptCategory}
                      disabled
                      className="input-dark mt-1 opacity-60"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Subcategory (optional)</Label>
                    <Input
                      id="bulkSubcategory"
                      placeholder="e.g., Plucked Strings"
                      className="input-dark mt-1"
                    />
                  </div>
                </div>
                
                <textarea
                  id="bulkImportData"
                  className="w-full h-48 bg-black/30 border border-white/10 rounded-lg p-4 text-white text-sm font-mono resize-none focus:border-primary focus:outline-none"
                  placeholder={`Acoustic Guitar: warm folk or pop rhythm
Electric Guitar: rock and blues tones, versatile
Bass Guitar: foundation for most genres`}
                />
                
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={async () => {
                      const textarea = document.getElementById('bulkImportData');
                      const subcategoryInput = document.getElementById('bulkSubcategory');
                      const data = textarea.value.trim();
                      const subcategory = subcategoryInput.value.trim();
                      
                      if (!data) {
                        toast.error('Please paste tag data first');
                        return;
                      }
                      
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/prompt-tags/import-bulk`, {
                          method: 'POST',
                          credentials: 'include',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ 
                            category: activePromptCategory,
                            subcategory: subcategory || null,
                            data 
                          })
                        });
                        const result = await res.json();
                        if (res.ok) {
                          toast.success(`Imported ${result.imported} tags! ${result.skipped > 0 ? `(${result.skipped} skipped)` : ''}`);
                          textarea.value = '';
                          // Refresh tags
                          const tagsRes = await nativeFetchWithAuth(`${API}/prompt-tags`, { credentials: 'include' });
                          const tagsData = await tagsRes.json();
                          setPromptTags(tagsData);
                        } else {
                          toast.error(result.detail || 'Import failed');
                        }
                      } catch (err) {
                        toast.error('Import failed: ' + err.message);
                      }
                    }}
                    className="bg-primary hover:bg-primary/90 text-black"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Import Tags
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      document.getElementById('bulkImportData').value = '';
                    }}
                    className="border-white/20 text-zinc-400 hover:text-white"
                  >
                    Clear
                  </Button>
                </div>
                {promptTags.filter(t => t.category === activePromptCategory).length > 0 && (
                  <p className="text-zinc-500 text-xs mt-3">
                    ✓ {promptTags.filter(t => t.category === activePromptCategory).length} tags currently in "{activePromptCategory}"
                  </p>
                )}
              </div>
            )}

            {/* Add New Tag Form */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Plus className="h-5 w-5 text-primary" />
                Add New Tag to "{activePromptCategory}"
              </h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs">Tag *</Label>
                  <Input
                    value={newTag.tag}
                    onChange={(e) => setNewTag({ ...newTag, tag: e.target.value })}
                    placeholder="e.g., [Verse]"
                    className="input-dark mt-1"
                    data-testid="new-tag-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Description *</Label>
                  <Input
                    value={newTag.description}
                    onChange={(e) => setNewTag({ ...newTag, description: e.target.value })}
                    placeholder="e.g., Main narrative"
                    className="input-dark mt-1"
                    data-testid="new-description-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Use Case</Label>
                  <Input
                    value={newTag.use_case}
                    onChange={(e) => setNewTag({ ...newTag, use_case: e.target.value })}
                    placeholder="e.g., Rock, pop"
                    className="input-dark mt-1"
                    data-testid="new-usecase-input"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Subcategory</Label>
                  <Input
                    value={newTag.subcategory}
                    onChange={(e) => setNewTag({ ...newTag, subcategory: e.target.value })}
                    placeholder="e.g., sections"
                    className="input-dark mt-1"
                    data-testid="new-subcategory-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    onClick={() => {
                      setNewTag({ ...newTag, category: activePromptCategory });
                      handleAddTag();
                    }} 
                    disabled={addingTag || !newTag.tag.trim() || !newTag.description.trim()} 
                    className="btn-primary w-full"
                    data-testid="add-tag-btn"
                  >
                    {addingTag ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Tags Table by Subcategory */}
            {(() => {
              const filteredTags = promptTags.filter(t => {
                const matchesCategory = t.category === activePromptCategory;
                const matchesSearch = !searchQuery || 
                  t.tag.toLowerCase().includes(searchQuery.toLowerCase()) ||
                  (t.description && t.description.toLowerCase().includes(searchQuery.toLowerCase()));
                return matchesCategory && matchesSearch;
              });
              
              // Group by subcategory
              const grouped = filteredTags.reduce((acc, tag) => {
                const sub = tag.subcategory || 'general';
                if (!acc[sub]) acc[sub] = [];
                acc[sub].push(tag);
                return acc;
              }, {});

              // Sort tags within each subcategory alphabetically
              Object.keys(grouped).forEach(key => {
                grouped[key].sort((a, b) => a.tag.localeCompare(b.tag));
              });

              return Object.entries(grouped).sort((a, b) => a[0].localeCompare(b[0])).map(([subcategory, tags]) => (
                <div key={subcategory} className="glass-card p-6">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2 capitalize">
                    <Tag className="h-5 w-5 text-primary" />
                    {subcategory.replace(/_/g, ' ')} ({tags.length})
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[700px]">
                      <thead>
                        <tr className="border-b border-white/10">
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium w-32">Tag</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium w-48">Subcategory</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium w-64">Description</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium">Use Case</th>
                          <th className="text-left py-3 px-4 text-zinc-400 font-medium w-24">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tags.map((tag) => (
                          <tr key={tag.tag_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4">
                              <code className="bg-primary/20 text-primary px-2 py-0.5 rounded text-xs font-mono">
                                {tag.tag}
                              </code>
                            </td>
                            <td className="py-3 px-4 text-zinc-400 text-xs capitalize">{(tag.subcategory || 'general').replace(/_/g, ' ')}</td>
                            <td className="py-3 px-4 text-zinc-300 text-sm w-64">{tag.description}</td>
                            <td className="py-3 px-4 text-zinc-400 text-sm">{tag.use_case}</td>
                            <td className="py-3 px-4">
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setEditingTag(tag)}
                                  className="text-zinc-400 hover:text-white hover:bg-white/10 h-8 w-8 p-0"
                                >
                                  <Edit2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteTagDialog(tag.tag_id, tag.tag)}
                                  disabled={deletingTagId === tag.tag_id}
                                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-8 w-8 p-0"
                                  data-testid={`delete-tag-${tag.tag_id}`}
                                >
                                  {deletingTagId === tag.tag_id ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="h-3.5 w-3.5" />
                                  )}
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ));
            })()}

            {promptTags.filter(t => t.category === activePromptCategory).length === 0 && (
              <div className="glass-card p-12 text-center">
                <Tag className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
                <p className="text-zinc-400">No tags in this category yet</p>
              </div>
            )}
          </div>
        )}

        {/* Song Options Tab */}
        {activeTab === 'songoptions' && (
          <div className="space-y-6" data-testid="songoptions-section">
            <div className="grid md:grid-cols-2 gap-6">
              {/* Moods */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-4">Moods</h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newMood}
                    onChange={(e) => setNewMood(e.target.value)}
                    placeholder="Add new mood..."
                    className="input-dark flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && addMood()}
                  />
                  <Button onClick={addMood} className="btn-primary">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {songMoods.map((mood) => (
                    <div key={mood} className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1">
                      <span className="text-white text-sm capitalize">{mood}</span>
                      <button
                        onClick={() => removeMood(mood)}
                        className="text-zinc-400 hover:text-red-400 ml-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {songMoods.length === 0 && (
                  <p className="text-zinc-500 text-sm">No moods added yet</p>
                )}
              </div>

              {/* Styles */}
              <div className="glass-card p-6">
                <h3 className="text-lg font-bold text-white mb-4">Styles</h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newGenre}
                    onChange={(e) => setNewGenre(e.target.value)}
                    placeholder="Add new style..."
                    className="input-dark flex-1"
                    onKeyDown={(e) => e.key === 'Enter' && addGenre()}
                  />
                  <Button onClick={addGenre} className="btn-primary">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {songGenres.map((genre) => (
                    <div key={genre} className="flex items-center gap-1 bg-white/10 rounded-full px-3 py-1">
                      <span className="text-white text-sm capitalize">{genre}</span>
                      <button
                        onClick={() => removeGenre(genre)}
                        className="text-zinc-400 hover:text-red-400 ml-1"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
                {songGenres.length === 0 && (
                  <p className="text-zinc-500 text-sm">No genres added yet</p>
                )}
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <Button
                onClick={handleSaveSongOptions}
                disabled={savingSongOptions}
                className="btn-primary"
              >
                {savingSongOptions ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Song Options</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Song Health Tab */}
        {activeTab === 'songhealth' && (
          <div className="space-y-6" data-testid="songhealth-section">
            <AudioMirrorCard API={API} nativeFetchWithAuth={nativeFetchWithAuth} />
            <CoverMirrorCard API={API} nativeFetchWithAuth={nativeFetchWithAuth} />
            {/* Health Summary */}
            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-primary" />
                  Song Audio Health
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      const ok = window.confirm(
                        'Scan every MusicGPT song with empty lyrics and try to refetch from /byId? This may take a minute (200ms throttle per song).'
                      );
                      if (!ok) return;
                      const t = toast.loading('Scanning MusicGPT songs for missing lyrics…');
                      try {
                        const r = await nativeFetchWithAuth(`${API}/admin/musicgpt/songs/backfill-all-lyrics`, {
                          method: 'POST',
                          credentials: 'include',
                        });
                        if (!r.ok) throw new Error(`HTTP ${r.status}`);
                        const d = await r.json();
                        toast.dismiss(t);
                        toast.success(
                          `Backfill complete — scanned ${d.scanned}, recovered ${d.recovered}, instrumental ${d.instrumental}, failed ${d.failed}`,
                          { duration: 8000 }
                        );
                      } catch (e) {
                        toast.dismiss(t);
                        toast.error(`Backfill failed: ${e.message}`);
                      }
                    }}
                    className="border-purple-500/40 text-purple-200 hover:bg-purple-500/10"
                    data-testid="backfill-all-lyrics-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Backfill MusicGPT Lyrics
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setLoadingSongs(true);
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/songs-health`, { credentials: 'include' });
                        if (res.ok) {
                          setSongHealth(await res.json());
                        }
                      } catch (e) {
                        toast.error('Failed to load health data');
                      }
                      setLoadingSongs(false);
                    }}
                    className="border-white/20 text-zinc-300"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${loadingSongs ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>
              </div>

              {!songHealth ? (
                <div className="text-center py-8">
                  <Button
                    onClick={async () => {
                      setLoadingSongs(true);
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/songs-health`, { credentials: 'include' });
                        if (res.ok) {
                          setSongHealth(await res.json());
                        }
                      } catch (e) {
                        toast.error('Failed to load health data');
                      }
                      setLoadingSongs(false);
                    }}
                    className="btn-primary"
                    disabled={loadingSongs}
                  >
                    {loadingSongs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <HeartPulse className="h-4 w-4 mr-2" />}
                    Load Song Health Data
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-white">{songHealth.total_songs}</div>
                    <div className="text-sm text-zinc-400">Total Songs</div>
                  </div>
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                    <div className="text-2xl font-bold text-green-400">{songHealth.hp_server_urls}</div>
                    <div className="text-sm text-green-400/70">Safe (HP Server)</div>
                  </div>
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <div className="text-2xl font-bold text-red-400">{songHealth.at_risk}</div>
                    <div className="text-sm text-red-400/70">At Risk (Temp URLs)</div>
                  </div>
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <div className="text-2xl font-bold text-zinc-400">{songHealth.no_audio_url}</div>
                    <div className="text-sm text-zinc-500">No Audio URL</div>
                  </div>
                </div>
              )}
              
              {songHealth && songHealth.at_risk > 0 && (
                <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-amber-400 font-medium">Warning: {songHealth.at_risk} songs have temporary SUNO URLs</p>
                      <p className="text-amber-400/70 text-sm mt-1">
                        These URLs will expire soon! Use the migration tool to save them to your HP server.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Migration Tool */}
              {songHealth && songHealth.at_risk > 0 && (
                <div className="mt-6 p-6 bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-lg">
                  <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Download className="h-5 w-5 text-purple-400" />
                    Migrate SUNO Audio to HP Server
                  </h4>
                  <p className="text-zinc-400 text-sm mb-4">
                    This will download audio from SUNO, embed cover art, and upload to your HP server permanently.
                    Songs are processed in batches of 20.
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-4">
                    <Button
                      onClick={async () => {
                        setMigrating(true);
                        setMigrationResults(null);
                        try {
                          const res = await nativeFetchWithAuth(`${API}/admin/migrate-suno-audio?limit=20&dry_run=false`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (res.ok) {
                            const data = await res.json();
                            setMigrationResults(data);
                            toast.success(`Migrated ${data.stats.migrated} songs! ${data.stats.expired} expired, ${data.stats.failed} failed.`);
                            // Refresh health data
                            const healthRes = await nativeFetchWithAuth(`${API}/admin/songs-health`, { credentials: 'include' });
                            if (healthRes.ok) {
                              setSongHealth(await healthRes.json());
                            }
                          } else {
                            toast.error('Migration failed');
                          }
                        } catch (e) {
                          toast.error('Migration error: ' + e.message);
                        }
                        setMigrating(false);
                      }}
                      disabled={migrating}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      {migrating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          Migrating...
                        </>
                      ) : (
                        <>
                          <Download className="h-4 w-4 mr-2" />
                          Migrate 20 Songs
                        </>
                      )}
                    </Button>
                    
                    <span className="text-zinc-500 text-sm">
                      {songHealth.at_risk} songs waiting to migrate
                    </span>
                  </div>

                  {/* Migration Results */}
                  {migrationResults && (
                    <div className="mt-4 p-4 bg-zinc-800/50 rounded-lg">
                      <h5 className="font-medium text-white mb-2">Migration Results:</h5>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm mb-3">
                        <div className="text-center">
                          <div className="text-xl font-bold text-green-400">{migrationResults.stats.migrated}</div>
                          <div className="text-zinc-500">Migrated</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-amber-400">{migrationResults.stats.expired}</div>
                          <div className="text-zinc-500">Expired</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-red-400">{migrationResults.stats.failed}</div>
                          <div className="text-zinc-500">Failed</div>
                        </div>
                        <div className="text-center">
                          <div className="text-xl font-bold text-zinc-400">{migrationResults.stats.found}</div>
                          <div className="text-zinc-500">Found</div>
                        </div>
                      </div>
                      
                      {/* Individual results */}
                      <div className="max-h-48 overflow-y-auto space-y-1">
                        {migrationResults.results.map((r, i) => (
                          <div key={i} className={`text-xs p-2 rounded ${
                            r.status === 'migrated' ? 'bg-green-500/10 text-green-400' :
                            r.status === 'expired' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-red-500/10 text-red-400'
                          }`}>
                            <span className="font-mono">{r.song_id}</span>: {r.title} - {r.status}
                            {r.error && <span className="ml-2 opacity-70">({r.error})</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cleanup Tools for Broken Songs */}
              {songHealth && (songHealth.at_risk > 0 || songHealth.no_audio_url > 0) && (
                <div className="mt-6 p-6 bg-gradient-to-r from-red-500/10 to-orange-500/10 border border-red-500/30 rounded-lg">
                  <h4 className="text-lg font-bold text-white mb-2 flex items-center gap-2">
                    <Trash2 className="h-5 w-5 text-red-400" />
                    Cleanup Broken Songs
                  </h4>
                  <p className="text-zinc-400 text-sm mb-4">
                    Songs with expired SUNO URLs cannot be recovered. You can mark them as failed (users see error message) or delete them permanently.
                  </p>
                  
                  <div className="flex flex-wrap items-center gap-4">
                    <Button
                      onClick={async () => {
                        if (!confirm('Mark all songs with expired URLs as "failed"? Users will see an error message asking them to regenerate.')) return;
                        try {
                          const res = await nativeFetchWithAuth(`${API}/admin/cleanup-broken-songs?action=mark_failed`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(`Marked ${data.stats.processed} songs as failed`);
                            // Refresh health data
                            const healthRes = await nativeFetchWithAuth(`${API}/admin/songs-health`, { credentials: 'include' });
                            if (healthRes.ok) {
                              setSongHealth(await healthRes.json());
                            }
                          } else {
                            toast.error('Cleanup failed');
                          }
                        } catch (e) {
                          toast.error('Cleanup error: ' + e.message);
                        }
                      }}
                      variant="outline"
                      className="bg-amber-500/20 border-amber-500/30 hover:bg-amber-500/30 text-amber-400"
                    >
                      <AlertTriangle className="h-4 w-4 mr-2" />
                      Mark as Failed
                    </Button>
                    
                    <Button
                      onClick={async () => {
                        if (!confirm('PERMANENTLY DELETE all songs with expired URLs? This cannot be undone!')) return;
                        if (!confirm('Are you absolutely sure? This will delete the songs from the database.')) return;
                        try {
                          const res = await nativeFetchWithAuth(`${API}/admin/cleanup-broken-songs?action=delete`, {
                            method: 'POST',
                            credentials: 'include'
                          });
                          if (res.ok) {
                            const data = await res.json();
                            toast.success(`Deleted ${data.stats.processed} broken songs`);
                            // Refresh health data
                            const healthRes = await nativeFetchWithAuth(`${API}/admin/songs-health`, { credentials: 'include' });
                            if (healthRes.ok) {
                              setSongHealth(await healthRes.json());
                            }
                          } else {
                            toast.error('Cleanup failed');
                          }
                        } catch (e) {
                          toast.error('Cleanup error: ' + e.message);
                        }
                      }}
                      variant="outline"
                      className="bg-red-500/20 border-red-500/30 hover:bg-red-500/30 text-red-400"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Permanently
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Song Browser */}
            <div className="glass-card p-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <h3 className="text-lg font-bold text-white">Browse & Edit Songs</h3>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
                    <Input
                      placeholder="Search by song ID..."
                      value={songSearchQuery}
                      onChange={(e) => setSongSearchQuery(e.target.value)}
                      className="input-dark pl-10 w-48"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showBrokenOnly}
                      onChange={(e) => setShowBrokenOnly(e.target.checked)}
                      className="rounded border-zinc-600"
                    />
                    Broken only
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      setLoadingSongs(true);
                      try {
                        const url = showBrokenOnly 
                          ? `${API}/admin/songs?broken_only=true&limit=100`
                          : `${API}/admin/songs?limit=100`;
                        const res = await fetch(url, { credentials: 'include' });
                        if (res.ok) {
                          const data = await res.json();
                          setAllSongs(data.songs || []);
                          toast.success(`Loaded ${data.songs?.length || 0} songs`);
                        }
                      } catch (e) {
                        toast.error('Failed to load songs');
                      }
                      setLoadingSongs(false);
                    }}
                    className="border-white/20 text-zinc-300"
                    disabled={loadingSongs}
                  >
                    {loadingSongs ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download className="h-4 w-4 mr-2" />}
                    Load Songs
                  </Button>
                </div>
              </div>

              {/* Direct Song ID Lookup */}
              <div className="mb-6 p-4 bg-zinc-800/50 rounded-lg">
                <Label className="text-white mb-2 block">Quick Lookup by Song ID</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="e.g., song_420b1ffa9ba5"
                    value={songSearchQuery}
                    onChange={(e) => setSongSearchQuery(e.target.value)}
                    className="input-dark flex-1"
                  />
                  <Button
                    onClick={async () => {
                      if (!songSearchQuery.trim()) return;
                      setLoadingSongs(true);
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/songs/${songSearchQuery.trim()}`, { credentials: 'include' });
                        if (res.ok) {
                          const data = await res.json();
                          setSelectedSong(data);
                          setEditingSongData({
                            audio_url: data.song?.audio_url || '',
                            image_url: data.song?.image_url || '',
                            title: data.song?.title || '',
                            lyrics: data.song?.lyrics || '',
                            locked: data.song?.locked || false,
                            is_public: data.song?.is_public || false,
                            added_from_library: data.song?.added_from_library || false
                          });
                          toast.success('Song loaded');
                        } else {
                          toast.error('Song not found');
                        }
                      } catch (e) {
                        toast.error('Failed to fetch song');
                      }
                      setLoadingSongs(false);
                    }}
                    className="btn-primary"
                    disabled={loadingSongs || !songSearchQuery.trim()}
                  >
                    {loadingSongs ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              {/* Songs Table */}
              {allSongs.length > 0 && (() => {
                const cycleSongSort = (key) => {
                  setSongSort(prev => {
                    if (prev.key !== key) return { key, dir: 'asc' };
                    if (prev.dir === 'asc') return { key, dir: 'desc' };
                    return { key: null, dir: null };
                  });
                };
                const SongSortIcon = ({ col }) => {
                  if (songSort.key !== col) return <ArrowUpDown className="h-3.5 w-3.5 opacity-40" />;
                  return songSort.dir === 'asc'
                    ? <ArrowUp className="h-3.5 w-3.5 text-primary" />
                    : <ArrowDown className="h-3.5 w-3.5 text-primary" />;
                };
                const SongHeaderBtn = ({ col, children, testid }) => (
                  <button
                    type="button"
                    data-testid={testid}
                    onClick={() => cycleSongSort(col)}
                    className="inline-flex items-center gap-1.5 text-zinc-400 font-medium hover:text-white transition-colors cursor-pointer select-none focus:outline-none focus:text-white"
                  >
                    <span>{children}</span>
                    <SongSortIcon col={col} />
                  </button>
                );
                // Bucket helper: maps audio_url → ordered status enum so
                // "Status" sort surfaces broken songs at the top in asc.
                const statusRank = (s) => {
                  const u = (s.audio_url || '').toLowerCase();
                  if (!u) return 1;                              // No Audio (worst)
                  if (u.includes('tempfile.aiquickdraw.com')) return 2; // At Risk
                  if (u.includes('suno.ai')) return 3;           // SUNO CDN
                  if (u.includes('media.wrappedinmusic.com')) return 5; // Safe (best)
                  return 4;                                      // Other
                };
                let displayedSongs = allSongs;
                if (songSort.key) {
                  const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });
                  const keyMap = {
                    song: s => (s.title || '~~~').toLowerCase(),
                    audio: s => (s.audio_url || '~~~').toLowerCase(),
                    status: s => statusRank(s),
                  };
                  const fn = keyMap[songSort.key];
                  displayedSongs = [...displayedSongs].sort((a, b) => {
                    const av = fn(a); const bv = fn(b);
                    let cmp;
                    if (typeof av === 'number' && typeof bv === 'number') {
                      cmp = av - bv;
                    } else {
                      cmp = collator.compare(String(av), String(bv));
                    }
                    return songSort.dir === 'asc' ? cmp : -cmp;
                  });
                }

                return (
                <div className="overflow-x-auto mb-6">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-4">
                          <SongHeaderBtn col="song" testid="admin-songs-sort-song">Song</SongHeaderBtn>
                        </th>
                        <th className="text-left py-3 px-4">
                          <SongHeaderBtn col="audio" testid="admin-songs-sort-audio">Audio URL</SongHeaderBtn>
                        </th>
                        <th className="text-left py-3 px-4">
                          <SongHeaderBtn col="status" testid="admin-songs-sort-status">Status</SongHeaderBtn>
                        </th>
                        <th className="text-left text-zinc-400 font-medium py-3 px-4">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayedSongs.map((song) => {
                        const audioUrl = song.audio_url || '';
                        const isHP = audioUrl.includes('media.wrappedinmusic.com');
                        const isTemp = audioUrl.includes('tempfile.aiquickdraw.com');
                        const isSunoCDN = audioUrl.includes('suno.ai');
                        const isEmpty = !audioUrl;
                        
                        return (
                          <tr key={song.song_id} className="border-b border-white/5 hover:bg-white/5">
                            <td className="py-3 px-4">
                              <div className="text-white font-medium text-sm">{song.title}</div>
                              <div className="text-zinc-500 text-xs font-mono">{song.song_id}</div>
                            </td>
                            <td className="py-3 px-4">
                              <div className="text-xs font-mono text-zinc-400 max-w-xs truncate">
                                {audioUrl || <span className="text-red-400">No URL</span>}
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              {isHP ? (
                                <span className="px-2 py-1 rounded text-xs bg-green-500/20 text-green-400">Safe</span>
                              ) : isTemp ? (
                                <span className="px-2 py-1 rounded text-xs bg-red-500/20 text-red-400">At Risk</span>
                              ) : isSunoCDN ? (
                                <span className="px-2 py-1 rounded text-xs bg-amber-500/20 text-amber-400">SUNO CDN</span>
                              ) : isEmpty ? (
                                <span className="px-2 py-1 rounded text-xs bg-zinc-500/20 text-zinc-400">No Audio</span>
                              ) : (
                                <span className="px-2 py-1 rounded text-xs bg-blue-500/20 text-blue-400">Other</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    setTestingAudioId(song.song_id);
                                    try {
                                      const res = await nativeFetchWithAuth(`${API}/admin/songs/${song.song_id}`, { credentials: 'include' });
                                      if (res.ok) {
                                        const data = await res.json();
                                        setSelectedSong(data);
                                        setEditingSongData({
                                          audio_url: data.song?.audio_url || '',
                                          image_url: data.song?.image_url || '',
                                          title: data.song?.title || '',
                                          locked: data.song?.locked || false,
                                          is_public: data.song?.is_public || false
                                        });
                                      }
                                    } catch (e) {
                                      toast.error('Failed to load song');
                                    }
                                    setTestingAudioId(null);
                                  }}
                                  className="border-white/20 text-xs px-2 py-1 h-7"
                                >
                                  {testingAudioId === song.song_id ? <Loader2 className="h-3 w-3 animate-spin" /> : 'View/Edit'}
                                </Button>
                                {audioUrl && (
                                  <a
                                    href={audioUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center px-2 py-1 rounded text-xs border border-white/20 text-zinc-400 hover:bg-white/10"
                                  >
                                    <ExternalLink className="h-3 w-3" />
                                  </a>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                );
              })()}

              {/* Selected Song Editor */}
              {selectedSong && (
                <div className="border border-white/10 rounded-lg p-6 bg-zinc-800/30">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-semibold text-white">
                      Editing: {selectedSong.song?.title}
                    </h4>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedSong(null);
                        setEditingSongData(null);
                      }}
                      className="text-zinc-400"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Song Info */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-zinc-400 text-sm">Song ID</Label>
                        <div className="font-mono text-white bg-black/30 px-3 py-2 rounded mt-1">
                          {selectedSong.song?.song_id}
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Audio Status</Label>
                        <div className={`flex items-center gap-2 mt-1 ${
                          selectedSong.audio_status === 'working' ? 'text-green-400' :
                          selectedSong.audio_status === 'missing' ? 'text-zinc-400' :
                          'text-red-400'
                        }`}>
                          {selectedSong.audio_status === 'working' ? (
                            <CheckCircle className="h-5 w-5" />
                          ) : (
                            <XCircle className="h-5 w-5" />
                          )}
                          {selectedSong.audio_status}
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">SUNO Task ID</Label>
                        <div className="font-mono text-xs text-zinc-500 bg-black/30 px-3 py-2 rounded mt-1 break-all">
                          {selectedSong.song?.suno_task_id || 'N/A'}
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Created</Label>
                        <div className="text-zinc-400 text-sm mt-1">
                          {selectedSong.song?.created_at ? new Date(selectedSong.song.created_at).toLocaleString() : 'Unknown'}
                        </div>
                      </div>
                    </div>

                    {/* Editable Fields */}
                    <div className="space-y-4">
                      <div>
                        <Label className="text-zinc-400 text-sm">Title</Label>
                        <Input
                          value={editingSongData?.title || ''}
                          onChange={(e) => setEditingSongData({ ...editingSongData, title: e.target.value })}
                          className="input-dark mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Lyrics (for moderation)</Label>
                        <textarea
                          value={editingSongData?.lyrics || ''}
                          onChange={(e) => setEditingSongData({ ...editingSongData, lyrics: e.target.value })}
                          className="textarea-dark mt-1 w-full h-32 font-mono text-xs"
                          placeholder="Song lyrics..."
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Audio URL</Label>
                        <Input
                          value={editingSongData?.audio_url || ''}
                          onChange={(e) => setEditingSongData({ ...editingSongData, audio_url: e.target.value })}
                          className="input-dark mt-1 font-mono text-xs"
                          placeholder="https://media.wrappedinmusic.com/audio/..."
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-sm">Image URL</Label>
                        <Input
                          value={editingSongData?.image_url || ''}
                          onChange={(e) => setEditingSongData({ ...editingSongData, image_url: e.target.value })}
                          className="input-dark mt-1 font-mono text-xs"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="song-locked"
                          checked={editingSongData?.locked || false}
                          onChange={(e) => setEditingSongData({ ...editingSongData, locked: e.target.checked })}
                          className="rounded border-zinc-600"
                        />
                        <Label htmlFor="song-locked" className="text-zinc-400 text-sm cursor-pointer">Locked</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="song-public"
                          checked={editingSongData?.is_public || false}
                          onChange={(e) => setEditingSongData({ ...editingSongData, is_public: e.target.checked })}
                          className="rounded border-zinc-600"
                        />
                        <Label htmlFor="song-public" className="text-zinc-400 text-sm cursor-pointer">In Public Library</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="song-from-library"
                          checked={editingSongData?.added_from_library || false}
                          onChange={(e) => setEditingSongData({ ...editingSongData, added_from_library: e.target.checked })}
                          className="rounded border-zinc-600"
                        />
                        <Label htmlFor="song-from-library" className="text-zinc-400 text-sm cursor-pointer">Shows "From Library" badge</Label>
                      </div>
                    </div>
                  </div>
                  
                  {/* Audio Player */}
                  {editingSongData?.audio_url && (
                    <div className="mt-4 p-4 bg-black/30 rounded-lg">
                      <Label className="text-zinc-400 text-sm mb-2 block">Audio Preview</Label>
                      <audio 
                        controls 
                        src={editingSongData.audio_url}
                        className="w-full"
                        style={{ height: '40px' }}
                      >
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  )}

                  {/* Save Button */}
                  <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-white/10">
                    {editingSongData?.audio_url && (
                      <a
                        href={editingSongData.audio_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary inline-flex items-center gap-2"
                      >
                        <ExternalLink className="h-4 w-4" />
                        Test URL
                      </a>
                    )}
                    <Button
                      onClick={async () => {
                        setSavingSong(true);
                        try {
                          const res = await nativeFetchWithAuth(`${API}/admin/songs/${selectedSong.song.song_id}`, {
                            method: 'PUT',
                            headers: { 'Content-Type': 'application/json' },
                            credentials: 'include',
                            body: JSON.stringify(editingSongData)
                          });
                          if (res.ok) {
                            toast.success('Song updated!');
                            // Refresh the selected song
                            const refreshRes = await nativeFetchWithAuth(`${API}/admin/songs/${selectedSong.song.song_id}`, { credentials: 'include' });
                            if (refreshRes.ok) {
                              setSelectedSong(await refreshRes.json());
                            }
                          } else {
                            const err = await res.json();
                            toast.error(err.detail || 'Failed to update song');
                          }
                        } catch (e) {
                          toast.error('Failed to save song');
                        }
                        setSavingSong(false);
                      }}
                      className="btn-primary"
                      disabled={savingSong}
                    >
                      {savingSong ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                      Save Changes
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Style Presets Tab */}
        {activeTab === 'stylepresets' && <AdminStylePresetsTab />}
        {activeTab === 'animation' && <AdminAnimationStudioTab />}
        {activeTab === 'coach' && <AdminCoachTab />}
        {activeTab === 'demofunnel' && <AdminDemoFunnelTab />}
        {activeTab === 'dbmigrate' && <AdminDbMigrateTab />}

        {/* Utilities Tab */}
        {activeTab === 'utilities' && (
          <div className="space-y-6" data-testid="utilities-section">
            <AdminMaintenanceModeCard />
            <AdminAtlasCloudStatus />
            <AdminPayPalStatus />
            {/* Cancelled Renders Account Setting */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-purple-400" />
                Cancelled Renders Account
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                User account where cancelled song renders are stored. Set this to a dedicated archive account.
              </p>
              
              <CancelledRendersAccountSetting />
            </div>

            {/* Cancelled Renders Section */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-orange-400" />
                Cancelled Renders
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Songs that were cancelled by users during generation. If SUNO completed them, they can be restored to the original user.
              </p>
              
              <CancelledRendersManager />
            </div>

            {/* Database Migrations Section */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <Settings className="h-5 w-5 text-blue-400" />
                Database Migrations
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                One-time database updates. Only run these if instructed.
              </p>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Fix Song Variations</p>
                    <p className="text-zinc-400 text-sm">Set variation=1 on existing songs without this field</p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/fix-song-variations`, { method: 'POST', credentials: 'include' });
                        const data = await res.json();
                        if (res.ok) {
                          toast.success(data.message);
                        } else {
                          toast.error(data.detail || 'Failed');
                        }
                      } catch (e) {
                        toast.error('Error: ' + e.message);
                      }
                    }}
                    variant="outline"
                    className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                  >
                    Run Migration
                  </Button>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg">
                  <div>
                    <p className="text-white font-medium">Fix Tag Brackets</p>
                    <p className="text-zinc-400 text-sm">Strip brackets from prompt tag names (e.g., [Violin] → Violin)</p>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/fix-tag-brackets`, { method: 'POST', credentials: 'include' });
                        const data = await res.json();
                        if (res.ok) {
                          toast.success(data.message);
                        } else {
                          toast.error(data.detail || 'Failed');
                        }
                      } catch (e) {
                        toast.error('Error: ' + e.message);
                      }
                    }}
                    variant="outline"
                    className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                  >
                    Run Migration
                  </Button>
                </div>
              </div>
            </div>

            {/* Changelogs Section */}
            <ChangelogViewer />

            {/* Recover Failed Videos */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-yellow-400" />
                Recover Failed Videos
              </h3>
              <p className="text-zinc-400 text-sm mb-4">
                Scans the HP server for videos that completed after the backend gave up polling. Recovers them back to the user's library.
              </p>
              <div className="flex gap-3">
                <Button
                  data-testid="recover-videos-btn"
                  onClick={async () => {
                    try {
                      const res = await nativeFetchWithAuth(`${API}/admin/recover-failed-videos`, { method: 'POST', credentials: 'include' });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message);
                      } else {
                        toast.error(data.detail || 'Failed');
                      }
                    } catch (e) {
                      toast.error('Error: ' + e.message);
                    }
                  }}
                  variant="outline"
                  className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                >
                  Scan & Recover
                </Button>
                <Button
                  data-testid="cleanup-failed-videos-btn"
                  onClick={async () => {
                    if (!window.confirm('Delete all failed videos from the database? This cannot be undone.')) return;
                    try {
                      const res = await nativeFetchWithAuth(`${API}/admin/cleanup-failed-videos`, { method: 'POST', credentials: 'include' });
                      const data = await res.json();
                      if (res.ok) {
                        toast.success(data.message);
                      } else {
                        toast.error(data.detail || 'Failed');
                      }
                    } catch (e) {
                      toast.error('Error: ' + e.message);
                    }
                  }}
                  variant="outline"
                  className="bg-red-900/20 border-red-500/20 hover:bg-red-900/40 text-red-300"
                >
                  Clean Up Failed
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* DB Backups Tab */}
        {activeTab === 'backups' && (
          <div className="space-y-6" data-testid="db-backups-section">
            <div className="glass-card p-6">
              <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
                <div>
                  <h3 className="text-lg font-bold text-white flex items-center gap-2">
                    <Database className="h-5 w-5 text-emerald-400" />
                    Database Backups
                  </h3>
                  <p className="text-sm text-zinc-400 mt-1">
                    Daily snapshots of every collection (gzipped JSON).
                    {' '}<span className="text-zinc-500">Auto-runs once every 24h • keeps last {dbBackupRetention} on rotation</span>
                  </p>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    onClick={triggerDbBackup}
                    disabled={dbBackupBusy}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white"
                    data-testid="db-backup-run-now-btn"
                  >
                    {dbBackupBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Database className="h-4 w-4 mr-2" />}
                    Run backup now
                  </Button>
                  {/* v2.9.58 — mongoimport-ready ZIP export (one JSONL per collection) */}
                  <Button
                    onClick={async () => {
                      try {
                        const res = await nativeFetchWithAuth(`${API}/admin/export-database`, {
                          method: 'POST',
                          credentials: 'include',
                        });
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        const blob = await res.blob();
                        const cd = res.headers.get('Content-Disposition') || '';
                        const m = cd.match(/filename="?([^";]+)"?/);
                        const fname = m ? m[1] : `wim-backup-${new Date().toISOString().slice(0,10)}.zip`;
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url; a.download = fname; a.click();
                        URL.revokeObjectURL(url);
                        toast.success(`Downloaded ${fname}`);
                      } catch (e) {
                        toast.error(`Export failed: ${e.message}`);
                      }
                    }}
                    variant="outline"
                    className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                    data-testid="db-export-zip-btn"
                    title="One JSONL file per collection + manifest, mongoimport-compatible"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export ZIP (migration)
                  </Button>
                </div>
              </div>

              <div className="rounded-xl border border-amber-500/20 bg-amber-500/[0.04] p-3 text-xs text-amber-200/90 mb-4">
                <strong>Heads-up:</strong> Backup files include user records with bcrypt password hashes (needed for full restore). Treat downloaded files as sensitive — store them encrypted at rest. <strong>For 7-day rotation on your own server, set a daily cron to download today's file via the API and rsync to your offsite location.</strong>
              </div>

              {dbBackups.length === 0 ? (
                <div className="text-zinc-500 text-sm py-8 text-center">
                  No backups yet. Click "Run backup now" to create the first one.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b border-white/10 text-zinc-400 text-xs uppercase tracking-wider">
                      <tr>
                        <th className="py-2 px-3 text-left">Filename</th>
                        <th className="py-2 px-3 text-left">Created</th>
                        <th className="py-2 px-3 text-right">Size</th>
                        <th className="py-2 px-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dbBackups.map((b, i) => (
                        <tr key={b.filename} className="border-b border-white/5 hover:bg-white/[0.02]">
                          <td className="py-3 px-3 text-white font-mono text-xs">
                            {b.filename}
                            {i === 0 && (
                              <span className="ml-2 px-1.5 py-0.5 rounded text-[10px] bg-emerald-500/20 text-emerald-300 font-medium">
                                latest
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-3 text-zinc-300 text-xs whitespace-nowrap">
                            {new Date(b.modified_at).toLocaleString()}
                          </td>
                          <td className="py-3 px-3 text-zinc-300 text-right whitespace-nowrap">
                            {(b.size_bytes / 1024 / 1024).toFixed(2)} MB
                          </td>
                          <td className="py-3 px-3 text-right whitespace-nowrap">
                            <Button
                              size="sm"
                              variant="outline"
                              className="bg-white/5 border-white/10 text-white hover:bg-white/10 mr-2"
                              onClick={() => downloadDbBackup(b.filename)}
                              data-testid={`db-backup-download-${b.filename}`}
                            >
                              <Download className="h-3 w-3 mr-1" />
                              Download
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-rose-400 hover:bg-rose-500/10 hover:text-rose-300"
                              onClick={() => deleteDbBackup(b.filename)}
                              data-testid={`db-backup-delete-${b.filename}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="glass-card p-6">
              <h4 className="text-white font-semibold mb-3">Daily download via cron (optional)</h4>
              <p className="text-xs text-zinc-400 mb-3">
                If you'd like to keep your own 7-day rotation on your home server, add a cron job that downloads today's backup once per day. The endpoint is admin-authenticated, so use the same admin login cookie or pass an API token (if your client supports it).
              </p>
              <pre className="bg-black/40 border border-white/10 rounded-lg p-3 text-[11px] text-emerald-300 overflow-x-auto">{`# /etc/cron.d/wim-db-backup  — runs daily at 03:00
0 3 * * * /usr/bin/curl -fsSL --cookie "session=YOUR_ADMIN_COOKIE" \\
  "https://wrappedinmusic.com/api/admin/db-backup/download/db_backup_$(date -u +\\%Y-\\%m-\\%d).json.gz" \\
  -o /home/admin/wim-backups/db_backup_$(date -u +\\%Y-\\%m-\\%d).json.gz \\
  && find /home/admin/wim-backups -name "db_backup_*.json.gz" -mtime +7 -delete`}</pre>
            </div>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="space-y-6" data-testid="feedback-section">
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                User Feedback ({feedbackList.length})
              </h3>
              
              {feedbackList.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 text-zinc-600" />
                  <p>No feedback submissions yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {feedbackList.map((fb) => (
                    <div key={fb.feedback_id} className="bg-black/20 rounded-lg p-4 border border-white/5 relative">
                      {/* Inline Delete Confirmation */}
                      {confirmDeleteFeedbackId === fb.feedback_id && (
                        <div className="absolute inset-0 bg-black/90 rounded-lg flex flex-col items-center justify-center gap-3 z-10">
                          <p className="text-white font-medium">Delete this feedback?</p>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => handleDeleteFeedback(fb.feedback_id)}
                              size="sm"
                              className="bg-red-500 hover:bg-red-600 text-white"
                            >
                              Yes, Delete
                            </Button>
                            <Button
                              onClick={() => setConfirmDeleteFeedbackId(null)}
                              size="sm"
                              variant="outline"
                              className="border-white/20 text-white hover:bg-white/10"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              fb.feedback_type === 'bug' 
                                ? 'bg-red-500/20 text-red-400' 
                                : fb.feedback_type === 'suggestion'
                                  ? 'bg-yellow-500/20 text-yellow-400'
                                  : fb.feedback_type === 'question'
                                    ? 'bg-blue-500/20 text-blue-400'
                                    : 'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {fb.feedback_type}
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                              fb.status === 'new' 
                                ? 'bg-green-500/20 text-green-400' 
                                : fb.status === 'reviewed'
                                  ? 'bg-blue-500/20 text-blue-400'
                                  : 'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {fb.status}
                            </span>
                          </div>
                          <h4 className="text-white font-medium">{fb.subject}</h4>
                          <p className="text-zinc-400 text-sm mt-2 whitespace-pre-wrap">{fb.description}</p>
                          <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                            <span>{fb.name}</span>
                            <span>{fb.email}</span>
                            <span>{new Date(fb.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                        
                        {/* Delete Button */}
                        <button
                          onClick={() => setConfirmDeleteFeedbackId(fb.feedback_id)}
                          disabled={deletingFeedbackId === fb.feedback_id}
                          className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                          title="Delete feedback"
                        >
                          {deletingFeedbackId === fb.feedback_id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Client Errors Tab */}
        {activeTab === 'errors' && (
          <div className="space-y-6" data-testid="client-errors-section">
            {/* Recover Song card — lives here so admins have a single place to
                triage errored songs. Pulls audio from SUNO using the task_id
                even when our DB record was lost or false-failed. */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <RefreshCw className="h-5 w-5 text-emerald-400" />
                Recover failed song
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Pulls a song's audio from SUNO and marks it completed in our DB. Use to rescue songs marked failed by the v2.9.18 race-condition or by backend outages.
                Provide either a song_id (we'll look up the task_id), or a SUNO task_id directly (in which case provide a user_id if no DB record exists).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3">
                <input
                  type="text"
                  value={recoverSongIdInput}
                  onChange={(e) => setRecoverSongIdInput(e.target.value)}
                  placeholder="song_xxxxxxxxxxxx"
                  className="px-3 py-2 bg-black/40 border border-white/10 rounded text-zinc-200 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                  data-testid="recover-song-id-input"
                />
                <input
                  type="text"
                  value={recoverTaskIdInput}
                  onChange={(e) => setRecoverTaskIdInput(e.target.value)}
                  placeholder="SUNO task_id (optional)"
                  className="px-3 py-2 bg-black/40 border border-white/10 rounded text-zinc-200 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                  data-testid="recover-task-id-input"
                />
                <input
                  type="text"
                  value={recoverUserIdInput}
                  onChange={(e) => setRecoverUserIdInput(e.target.value)}
                  placeholder="user_id (only if no DB record)"
                  className="px-3 py-2 bg-black/40 border border-white/10 rounded text-zinc-200 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                  data-testid="recover-user-id-input"
                />
              </div>
              <Button
                onClick={handleRecoverSong}
                disabled={recovering || (!recoverSongIdInput.trim() && !recoverTaskIdInput.trim())}
                className="bg-emerald-500 hover:bg-emerald-600 text-white"
                data-testid="recover-song-btn"
              >
                {recovering ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Recovering…</>
                ) : (
                  <><RefreshCw className="h-4 w-4 mr-2" /> Recover from SUNO</>
                )}
              </Button>

              {recoverResult && (
                <div className={`mt-4 p-4 rounded-lg border text-sm ${
                  recoverResult.result === 'recovered' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'
                  : recoverResult.result === 'still_processing' ? 'bg-amber-500/10 border-amber-500/30 text-amber-200'
                  : 'bg-rose-500/10 border-rose-500/30 text-rose-200'
                }`} data-testid="recover-result">
                  <div className="font-semibold capitalize mb-1">{(recoverResult.result || 'unknown').replace(/_/g, ' ')}</div>
                  <div className="text-zinc-300">{recoverResult.message}</div>
                  {recoverResult.song_id && (
                    <div className="mt-2 font-mono text-xs text-zinc-400">song_id: {recoverResult.song_id}</div>
                  )}
                  {recoverResult.audio_url && (
                    <a href={recoverResult.audio_url} target="_blank" rel="noopener noreferrer" className="block mt-1 text-xs text-emerald-300 hover:underline truncate">
                      {recoverResult.audio_url}
                    </a>
                  )}
                </div>
              )}
            </div>

            {/* HP Audio Storage Audit / Repair / Transfer */}
            <div className="glass-card p-6">
              <h3 className="text-lg font-bold text-white flex items-center gap-2 mb-2">
                <Database className="h-5 w-5 text-amber-400" />
                HP audio storage health
              </h3>
              <p className="text-xs text-zinc-500 mb-4">
                Find songs whose <code>audio_url</code> still points at SUNO's CDN (which expires after ~30 days).
                Three actions: audit (read-only count), repair (flip pointer if file already on HP), transfer (download from SUNO + upload to HP for still-alive URLs).
              </p>

              <div className="flex flex-wrap gap-2 mb-4">
                <Button onClick={handleHpAudit} disabled={hpAuditing} variant="outline" className="border-zinc-700 text-zinc-200" data-testid="hp-audit-btn">
                  {hpAuditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Audit
                </Button>
                <Button onClick={handleHpRepair} disabled={hpRepairing} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="hp-repair-btn">
                  {hpRepairing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Repair pointers
                </Button>
              </div>

              {hpAuditResult && (
                <div className="bg-black/30 border border-white/5 rounded p-3 mb-3 text-sm" data-testid="hp-audit-result">
                  <div className="font-semibold text-zinc-200 mb-2">Audit summary</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div className="text-emerald-300">already_on_hp: <b>{hpAuditResult.summary?.already_on_hp ?? 0}</b></div>
                    <div className="text-blue-300">transferable: <b>{hpAuditResult.summary?.transferable ?? 0}</b></div>
                    <div className="text-rose-300">lost: <b>{hpAuditResult.summary?.lost ?? 0}</b></div>
                    <div className="text-amber-300">check_error: <b>{hpAuditResult.summary?.check_error ?? 0}</b></div>
                  </div>
                  <div className="text-xs text-zinc-500 mt-2">Total SUNO URLs in DB: {hpAuditResult.summary?.total_suno_urls_in_db ?? 0}</div>
                </div>
              )}

              {hpRepairResult && (
                <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 mb-3 text-sm text-emerald-200" data-testid="hp-repair-result">
                  ✅ Repaired {hpRepairResult.repaired_count} pointers · Skipped {hpRepairResult.skipped_count}
                </div>
              )}
            </div>

            <div className="glass-card p-6">
              <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
                <h3 className="text-lg font-bold text-white flex items-center gap-2">
                  <Bug className="h-5 w-5 text-rose-400" />
                  Client Errors ({clientErrorsTotal})
                </h3>
                <div className="flex gap-2">
                  <Button
                    onClick={loadClientErrors}
                    variant="outline"
                    size="sm"
                    className="border-zinc-700 text-zinc-300"
                    disabled={clientErrorsLoading}
                    data-testid="refresh-client-errors-btn"
                  >
                    {clientErrorsLoading ? (
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-1" />
                    )}
                    Refresh
                  </Button>
                  {clientErrors.length > 0 && (
                    <Button
                      onClick={() => setConfirmClearErrors(true)}
                      variant="outline"
                      size="sm"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/10"
                      data-testid="clear-client-errors-btn"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Clear all
                    </Button>
                  )}
                </div>
              </div>

              <p className="text-xs text-zinc-500 mb-4">
                Render-time crashes captured by the app's ErrorBoundary. Most recent first.
                {clientErrorsTotal > clientErrors.length && ` Showing ${clientErrors.length} of ${clientErrorsTotal}.`}
              </p>

              {confirmClearErrors && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-4 flex items-center justify-between gap-4">
                  <p className="text-sm text-red-300">Delete all {clientErrorsTotal} error logs? This can't be undone.</p>
                  <div className="flex gap-2">
                    <Button onClick={handleClearClientErrors} size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                      Yes, clear all
                    </Button>
                    <Button onClick={() => setConfirmClearErrors(false)} size="sm" variant="outline" className="border-white/20 text-white">
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              {clientErrorsLoading && clientErrors.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <Loader2 className="h-8 w-8 mx-auto mb-2 animate-spin" />
                  <p>Loading…</p>
                </div>
              ) : clientErrors.length === 0 ? (
                <div className="text-center py-12 text-zinc-400">
                  <Bug className="h-12 w-12 mx-auto mb-4 text-zinc-600" />
                  <p>No client errors logged yet — nice and quiet.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {clientErrors.map((err) => {
                    const expanded = expandedErrorId === err.error_id;
                    let when = err.created_at;
                    try { when = new Date(err.created_at).toLocaleString(); } catch (_) { /* keep iso */ }
                    return (
                      <div key={err.error_id} className="bg-black/30 rounded-lg border border-white/5">
                        <button
                          onClick={() => setExpandedErrorId(expanded ? null : err.error_id)}
                          className="w-full text-left p-4 hover:bg-white/5 transition-colors"
                          data-testid={`client-error-${err.error_id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-xs px-2 py-0.5 rounded bg-rose-500/20 text-rose-300 font-mono">
                                  {err.version || 'unknown'}
                                </span>
                                <span className="text-xs text-zinc-500">{when}</span>
                                {err.user_id && (
                                  <span className="text-xs text-zinc-500 truncate">user: {err.user_id}</span>
                                )}
                              </div>
                              <p className="text-sm text-zinc-200 font-mono truncate">{err.message || '(no message)'}</p>
                              {err.url && <p className="text-xs text-zinc-500 truncate mt-1">{err.url}</p>}
                            </div>
                            <div className="text-zinc-500 text-xs shrink-0">{expanded ? '▾' : '▸'}</div>
                          </div>
                        </button>
                        {expanded && (
                          <div className="border-t border-white/5 p-4 space-y-3 text-xs">
                            {err.stack && (
                              <div>
                                <div className="text-zinc-400 font-medium mb-1">Stack</div>
                                <pre className="bg-black/40 p-3 rounded text-zinc-300 whitespace-pre-wrap break-words font-mono max-h-64 overflow-auto">{err.stack}</pre>
                              </div>
                            )}
                            {err.component_stack && (
                              <div>
                                <div className="text-zinc-400 font-medium mb-1">Component stack</div>
                                <pre className="bg-black/40 p-3 rounded text-zinc-300 whitespace-pre-wrap break-words font-mono max-h-48 overflow-auto">{err.component_stack}</pre>
                              </div>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-zinc-500">
                              <div><span className="text-zinc-600">error_id:</span> <span className="font-mono text-zinc-400">{err.error_id}</span></div>
                              {err.ip && <div><span className="text-zinc-600">ip:</span> <span className="font-mono text-zinc-400">{err.ip}</span></div>}
                              {err.user_agent && <div className="md:col-span-2"><span className="text-zinc-600">UA:</span> <span className="font-mono text-zinc-400 break-all">{err.user_agent}</span></div>}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Birthday Names Tab */}
        {activeTab === 'birthdaynames' && (
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Cake className="h-5 w-5 text-pink-400" />
                Requested Birthday Names
              </h3>
              <div className="text-sm text-zinc-400">
                {nameRequests.total_unique_names} unique names • {nameRequests.total_requests} total requests
              </div>
            </div>
            
            <p className="text-zinc-400 text-sm mb-4">
              Names users have requested that aren't in the personalized list. Use this to prioritize which names to record next.
            </p>
            
            {nameRequests.requests.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                No name requests yet. Users will appear here when they use names not in the personalized list.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">Rank</th>
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">Name</th>
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">Requests</th>
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">First Requested</th>
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">Last Requested</th>
                      <th className="text-left text-zinc-400 font-medium py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {nameRequests.requests.map((req, index) => (
                      <tr key={req.name} className="border-b border-white/5 hover:bg-white/5">
                        <td className="py-3 px-4 text-zinc-500">#{index + 1}</td>
                        <td className="py-3 px-4 text-white font-medium capitalize">{req.name}</td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded text-sm ${
                            req.count >= 10 ? 'bg-green-500/20 text-green-400' :
                            req.count >= 5 ? 'bg-yellow-500/20 text-yellow-400' :
                            'bg-zinc-500/20 text-zinc-400'
                          }`}>
                            {req.count}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-zinc-400 text-sm">
                          {req.first_requested ? new Date(req.first_requested).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4 text-zinc-400 text-sm">
                          {req.last_requested ? new Date(req.last_requested).toLocaleDateString() : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={async () => {
                              if (confirm(`Clear "${req.name}" from requests? (Do this after adding it to personalized list)`)) {
                                try {
                                  const res = await nativeFetchWithAuth(`${API}/admin/birthday-name-requests/${req.name}`, {
                                    method: 'DELETE',
                                    credentials: 'include'
                                  });
                                  if (res.ok) {
                                    toast.success(`Cleared "${req.name}" from requests`);
                                    // Refresh data
                                    const nameReqRes = await nativeFetchWithAuth(`${API}/admin/birthday-name-requests`, { credentials: 'include' });
                                    if (nameReqRes.ok) {
                                      setNameRequests(await nameReqRes.json());
                                    }
                                  }
                                } catch (error) {
                                  toast.error('Failed to clear name');
                                }
                              }
                            }}
                            className="text-red-400 hover:text-red-300 text-sm"
                          >
                            Clear
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Edit Tag Modal */}
        <Dialog open={!!editingTag} onOpenChange={(open) => !open && setEditingTag(null)}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Tag</DialogTitle>
            </DialogHeader>
            
            {editingTag && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-400">Tag</Label>
                  <Input
                    value={editingTag.tag}
                    onChange={(e) => setEditingTag({ ...editingTag, tag: e.target.value })}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Description</Label>
                  <Input
                    value={editingTag.description || ''}
                    onChange={(e) => setEditingTag({ ...editingTag, description: e.target.value })}
                    className="input-dark mt-1"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400">Use Case</Label>
                  <Input
                    value={editingTag.use_case || ''}
                    onChange={(e) => setEditingTag({ ...editingTag, use_case: e.target.value })}
                    className="input-dark mt-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400">Category</Label>
                    <select
                      value={editingTag.category}
                      onChange={(e) => setEditingTag({ ...editingTag, category: e.target.value })}
                      className="custom-select w-full mt-1"
                    >
                      <option value="structure">Structure</option>
                      <option value="vocals">Vocals</option>
                      <option value="instruments">Instruments</option>
                      <option value="mood">Mood</option>
                      <option value="genres">Genres</option>
                      <option value="effects">Effects</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-zinc-400">Subcategory</Label>
                    <Input
                      value={editingTag.subcategory || ''}
                      onChange={(e) => setEditingTag({ ...editingTag, subcategory: e.target.value })}
                      className="input-dark mt-1"
                    />
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditingTag(null)} className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </Button>
              <Button onClick={handleUpdateTag} className="btn-primary">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>


        {/* Admin — Import MP3 for a specific user. Kept in the Users tab
            per user request so the target user is unambiguous. */}
        <Dialog open={!!importSongUser} onOpenChange={(open) => !open && setImportSongUser(null)}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-lg" data-testid="import-song-modal">
            <DialogHeader>
              <DialogTitle className="text-white">Import MP3 for {importSongUser?.name}</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Adds an existing recording to {importSongUser?.email}'s library as a completed song. All Song actions (Cover, Video, Gift, Animation) work off it afterwards.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2 max-h-[70vh] overflow-y-auto pr-1">
              <div>
                <Label className="text-white">Title *</Label>
                <input
                  type="text"
                  value={importSongForm.title}
                  onChange={(e) => setImportSongForm({ ...importSongForm, title: e.target.value })}
                  placeholder='e.g. "New York, New York"'
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm"
                  data-testid="import-title-input"
                />
              </div>
              <div>
                <Label className="text-white">Artist / Original credit <span className="text-zinc-500 text-xs">(optional)</span></Label>
                <input
                  type="text"
                  value={importSongForm.artist}
                  onChange={(e) => setImportSongForm({ ...importSongForm, artist: e.target.value })}
                  placeholder='e.g. "Original by Frank Sinatra"'
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm"
                  data-testid="import-artist-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white">Genre <span className="text-zinc-500 text-xs">(opt)</span></Label>
                  <input
                    type="text"
                    value={importSongForm.genre}
                    onChange={(e) => setImportSongForm({ ...importSongForm, genre: e.target.value })}
                    placeholder="Jazz"
                    className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm"
                  />
                </div>
                <div>
                  <Label className="text-white">Mood <span className="text-zinc-500 text-xs">(opt)</span></Label>
                  <input
                    type="text"
                    value={importSongForm.mood}
                    onChange={(e) => setImportSongForm({ ...importSongForm, mood: e.target.value })}
                    placeholder="Upbeat"
                    className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white">Audio file * <span className="text-zinc-500 text-xs">(mp3 / wav / m4a / aac / ogg / flac, ≤50MB)</span></Label>
                <input
                  type="file"
                  accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac"
                  onChange={(e) => setImportAudioFile(e.target.files?.[0] || null)}
                  className="w-full mt-1 text-white text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary/20 file:text-primary hover:file:bg-primary/30"
                  data-testid="import-audio-file"
                />
                {importAudioFile && (
                  <div className="text-xs text-zinc-400 mt-1">
                    {importAudioFile.name} · {(importAudioFile.size / 1024 / 1024).toFixed(1)} MB
                  </div>
                )}
              </div>
              <div>
                <Label className="text-white">Cover image <span className="text-zinc-500 text-xs">(optional, jpg/png/webp, ≤8MB)</span></Label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={(e) => setImportCoverFile(e.target.files?.[0] || null)}
                  className="w-full mt-1 text-white text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0 file:bg-primary/20 file:text-primary hover:file:bg-primary/30"
                  data-testid="import-cover-file"
                />
              </div>
              <div>
                <Label className="text-white">Lyrics <span className="text-zinc-500 text-xs">(optional — paste, or auto-transcribe below)</span></Label>
                <textarea
                  value={importSongForm.lyrics}
                  onChange={(e) => setImportSongForm({ ...importSongForm, lyrics: e.target.value })}
                  placeholder="Paste lyrics here, or leave blank and tick 'Auto-transcribe' below"
                  rows={4}
                  className="w-full mt-1 px-3 py-2 bg-black/40 border border-white/10 rounded text-white text-sm font-mono"
                  data-testid="import-lyrics-textarea"
                />
              </div>
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer">
                <input
                  type="checkbox"
                  checked={importSongForm.transcribe}
                  disabled={!!importSongForm.lyrics.trim()}
                  onChange={(e) => setImportSongForm({ ...importSongForm, transcribe: e.target.checked })}
                  data-testid="import-transcribe-checkbox"
                />
                Auto-transcribe lyrics if the field above is empty
                {importSongForm.lyrics.trim() && <span className="text-zinc-500 text-xs">(disabled — lyrics already provided)</span>}
              </label>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setImportSongUser(null)}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                disabled={importingSong}
              >
                Cancel
              </Button>
              <Button
                onClick={submitImportSong}
                disabled={importingSong || !importAudioFile || !importSongForm.title.trim()}
                className="btn-primary"
                data-testid="import-submit-btn"
              >
                {importingSong ? 'Importing…' : (<><Upload className="h-4 w-4 mr-2" />Import Song</>)}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Credits Modal */}
        <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
          <DialogContent className="bg-zinc-900 border-white/10 text-white sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white">Edit User Credits</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Update credits for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                <span className="text-zinc-400">Current Credits:</span>
                <span className="credit-badge text-lg">
                  <Zap className="h-4 w-4" />
                  {editingUser?.credits || 0}
                </span>
              </div>
              
              <div className="space-y-2">
                <Label className="text-zinc-300">Action</Label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={creditMode === 'set' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCreditMode('set')}
                    className={creditMode === 'set' ? 'btn-primary' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}
                  >
                    Set to
                  </Button>
                  <Button
                    type="button"
                    variant={creditMode === 'add' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCreditMode('add')}
                    className={creditMode === 'add' ? 'bg-green-600 hover:bg-green-700' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add
                  </Button>
                  <Button
                    type="button"
                    variant={creditMode === 'subtract' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setCreditMode('subtract')}
                    className={creditMode === 'subtract' ? 'bg-red-600 hover:bg-red-700' : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}
                  >
                    <Minus className="h-3 w-3 mr-1" />
                    Subtract
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="creditAmount" className="text-zinc-300">
                  {creditMode === 'set' ? 'New Credit Amount' : creditMode === 'add' ? 'Credits to Add' : 'Credits to Subtract'}
                </Label>
                <Input
                  id="creditAmount"
                  type="number"
                  min="0"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  className="input-dark"
                  placeholder="Enter amount"
                  data-testid="credit-amount-input"
                />
              </div>
              
              {creditAmount !== '' && (
                <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg">
                  <span className="text-zinc-300">New balance will be: </span>
                  <span className="font-bold text-primary">
                    {creditMode === 'set' 
                      ? parseInt(creditAmount) || 0
                      : creditMode === 'add'
                        ? (editingUser?.credits || 0) + (parseInt(creditAmount) || 0)
                        : Math.max(0, (editingUser?.credits || 0) - (parseInt(creditAmount) || 0))
                    } credits
                  </span>
                </div>
              )}
            </div>
            
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setEditingUser(null)}
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleUpdateCredits} 
                disabled={updatingCredits || creditAmount === ''}
                className="btn-primary"
                data-testid="save-credits-btn"
              >
                {updatingCredits ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Tag Confirmation Dialog */}
        <AlertDialog open={deleteTagDialog.open} onOpenChange={(open) => !open && closeDeleteTagDialog()}>
          <AlertDialogContent className="bg-zinc-900 border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-500" />
                Delete Prompt Tag?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-400">
                <span className="block mb-2">
                  Are you sure you want to delete the tag "<span className="text-white font-medium">{deleteTagDialog.tagName}</span>"?
                </span>
                <span className="block text-red-400 font-medium">
                  This action is permanent and cannot be undone.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleConfirmDeleteTag}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-tag-btn"
              >
                Delete Permanently
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* View User Songs Modal */}
        <Dialog open={!!viewingSongsUser} onOpenChange={(open) => !open && setViewingSongsUser(null)}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Music className="h-5 w-5 text-primary" />
                Songs for {viewingSongsUser?.name || viewingSongsUser?.email}
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                {userSongs.length} song(s) • Click "Reassign" to move a song to another user
              </DialogDescription>
            </DialogHeader>
            <div className="flex-1 overflow-y-auto mt-4">
              {loadingUserSongs ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                </div>
              ) : userSongs.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">No songs found</div>
              ) : (
                <div className="space-y-2">
                  {userSongs.map((song) => (
                    <div key={song.song_id} className="p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            {song.image_url ? (
                              <img src={song.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-black/30 flex items-center justify-center">
                                <Music className="h-5 w-5 text-white/30" />
                              </div>
                            )}
                            {/* Play button overlay */}
                            {song.audio_url && (
                              <button
                                onClick={() => {
                                  if (playingSongId === song.song_id) {
                                    userSongsAudioRef.current?.pause();
                                    setPlayingSongId(null);
                                  } else {
                                    setPlayingSongId(song.song_id);
                                  }
                                }}
                                className="absolute inset-0 flex items-center justify-center bg-black/50 rounded opacity-0 hover:opacity-100 transition-opacity"
                              >
                                {playingSongId === song.song_id ? (
                                  <Pause className="h-5 w-5 text-white" />
                                ) : (
                                  <Play className="h-5 w-5 text-white ml-0.5" />
                                )}
                              </button>
                            )}
                          </div>
                          <div>
                            <p className="text-white font-medium">{song.title || 'Untitled'}</p>
                            <p className="text-zinc-400 text-sm">
                              {song.status} • {new Date(song.created_at).toLocaleDateString()}
                              {song.is_public && <span className="ml-2 text-green-400">• Public</span>}
                              {song.added_from_library && <span className="ml-2 text-blue-400">• From Library</span>}
                              {song.variation === 2 && <span className="ml-2 text-purple-400">• Alt</span>}
                            </p>
                            {/* Original user info for library/cancelled songs */}
                            {song.original_user_id && (
                              <p className="text-xs text-amber-400/80 mt-0.5" title={`Original User ID: ${song.original_user_id}`}>
                                ↳ Original: {song.original_user_email || song.original_user_id}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              try {
                                const res = await nativeFetchWithAuth(`${API}/admin/songs/${song.song_id}`, {
                                  credentials: 'include'
                                });
                                const data = await res.json();
                                setEditSongDialog({ open: true, song: data.song });
                                setEditingSongData({
                                  audio_url: data.song?.audio_url || '',
                                  image_url: data.song?.image_url || '',
                                  title: data.song?.title || '',
                                  lyrics: data.song?.lyrics || '',
                                  locked: data.song?.locked || false,
                                  is_public: data.song?.is_public || false,
                                  added_from_library: data.song?.added_from_library || false
                                });
                              } catch (err) {
                                toast.error('Failed to load song');
                              }
                            }}
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!confirm(`Delete "${song.title || 'Untitled'}"? This cannot be undone.`)) return;
                              try {
                                await nativeFetchWithAuth(`${API}/admin/songs/${song.song_id}`, {
                                  method: 'DELETE',
                                  credentials: 'include'
                                });
                                toast.success('Song deleted');
                                setUserSongs(prev => prev.filter(s => s.song_id !== song.song_id));
                              } catch (err) {
                                toast.error('Failed to delete song');
                              }
                            }}
                            className="bg-red-500/10 border-red-500/20 hover:bg-red-500/20 text-red-400"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setReassignDialog({ open: true, song });
                              setTargetUserId('');
                            }}
                            className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
                          >
                            Reassign
                          </Button>
                        </div>
                      </div>
                      {/* Audio player - shown when this song is playing */}
                      {playingSongId === song.song_id && song.audio_url && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <audio
                            ref={userSongsAudioRef}
                            src={song.audio_url}
                            controls
                            autoPlay
                            className="w-full"
                            style={{ height: '32px' }}
                            onEnded={() => setPlayingSongId(null)}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Reassign Song Modal */}
        <Dialog open={reassignDialog.open} onOpenChange={(open) => !open && setReassignDialog({ open: false, song: null })}>
          <DialogContent className="bg-zinc-900 border-white/10">
            <DialogHeader>
              <DialogTitle className="text-white">Reassign Song</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Move "{reassignDialog.song?.title || 'Untitled'}" to a different user
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-white">Select Target User</Label>
                <select
                  value={targetUserId}
                  onChange={(e) => setTargetUserId(e.target.value)}
                  className="w-full mt-2 p-2 bg-black/30 border border-white/10 rounded-lg text-white"
                >
                  <option value="">-- Select User --</option>
                  {users
                    .filter(u => u.user_id !== viewingSongsUser?.user_id)
                    .map((u) => (
                      <option key={u.user_id} value={u.user_id}>
                        {u.name || u.email} ({u.email})
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setReassignDialog({ open: false, song: null })}
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReassignSong}
                disabled={!targetUserId || reassigning}
                className="bg-primary hover:bg-primary/90"
              >
                {reassigning ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Reassign Song
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Song Dialog */}
        <Dialog open={editSongDialog.open} onOpenChange={(open) => !open && setEditSongDialog({ open: false, song: null })}>
          <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-white">Edit Song</DialogTitle>
              <DialogDescription className="text-zinc-400">
                Editing: {editSongDialog.song?.title || 'Untitled'}
              </DialogDescription>
            </DialogHeader>
            
            {editingSongData && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-zinc-400 text-sm">Title</Label>
                  <Input
                    value={editingSongData.title || ''}
                    onChange={(e) => setEditingSongData({ ...editingSongData, title: e.target.value })}
                    className="input-dark mt-1"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400 text-sm">Lyrics (for moderation)</Label>
                  <textarea
                    value={editingSongData.lyrics || ''}
                    onChange={(e) => setEditingSongData({ ...editingSongData, lyrics: e.target.value })}
                    className="textarea-dark mt-1 w-full h-32 font-mono text-xs"
                    placeholder="Song lyrics..."
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400 text-sm">Audio URL</Label>
                  <Input
                    value={editingSongData.audio_url || ''}
                    onChange={(e) => setEditingSongData({ ...editingSongData, audio_url: e.target.value })}
                    className="input-dark mt-1 font-mono text-xs"
                  />
                </div>
                
                <div>
                  <Label className="text-zinc-400 text-sm">Image URL</Label>
                  <Input
                    value={editingSongData.image_url || ''}
                    onChange={(e) => setEditingSongData({ ...editingSongData, image_url: e.target.value })}
                    className="input-dark mt-1 font-mono text-xs"
                  />
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-song-locked"
                      checked={editingSongData.locked || false}
                      onChange={(e) => setEditingSongData({ ...editingSongData, locked: e.target.checked })}
                      className="rounded border-zinc-600"
                    />
                    <Label htmlFor="edit-song-locked" className="text-zinc-400 text-sm cursor-pointer">Locked</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-song-public"
                      checked={editingSongData.is_public || false}
                      onChange={(e) => setEditingSongData({ ...editingSongData, is_public: e.target.checked })}
                      className="rounded border-zinc-600"
                    />
                    <Label htmlFor="edit-song-public" className="text-zinc-400 text-sm cursor-pointer">In Public Library</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="edit-song-from-library"
                      checked={editingSongData.added_from_library || false}
                      onChange={(e) => setEditingSongData({ ...editingSongData, added_from_library: e.target.checked })}
                      className="rounded border-zinc-600"
                    />
                    <Label htmlFor="edit-song-from-library" className="text-zinc-400 text-sm cursor-pointer">Shows "From Library" badge</Label>
                  </div>
                </div>
                
                {/* Audio Preview */}
                {editingSongData.audio_url && (
                  <div className="p-4 bg-black/30 rounded-lg">
                    <Label className="text-zinc-400 text-sm mb-2 block">Audio Preview</Label>
                    <audio 
                      controls 
                      src={editingSongData.audio_url}
                      className="w-full"
                      style={{ height: '40px' }}
                    />
                  </div>
                )}
              </div>
            )}
            
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setEditSongDialog({ open: false, song: null })}
                className="bg-white/5 border-white/10 hover:bg-white/10 text-white"
              >
                Cancel
              </Button>
              <Button
                onClick={async () => {
                  try {
                    setSavingSong(true);
                    await nativeFetchWithAuth(`${API}/admin/songs/${editSongDialog.song?.song_id}`, {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      credentials: 'include',
                      body: JSON.stringify(editingSongData)
                    });
                    toast.success('Song updated');
                    // Update local state
                    setUserSongs(prev => prev.map(s => 
                      s.song_id === editSongDialog.song?.song_id 
                        ? { ...s, ...editingSongData } 
                        : s
                    ));
                    setEditSongDialog({ open: false, song: null });
                  } catch (err) {
                    toast.error('Failed to save song');
                  } finally {
                    setSavingSong(false);
                  }
                }}
                disabled={savingSong}
                className="bg-primary text-black hover:bg-primary/90"
              >
                {savingSong ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Saving...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Save Changes</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Confirmation Dialog */}
        <AlertDialog open={deleteUserDialog.open} onOpenChange={(open) => setDeleteUserDialog({ open, user: deleteUserDialog.user })}>
          <AlertDialogContent className="bg-zinc-900 border-red-500/30">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-red-400 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Delete User Account?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-zinc-300">
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mt-2">
                  <p className="text-red-400 font-semibold mb-2">
                    This action cannot be undone!
                  </p>
                  <p className="text-zinc-400 text-sm mb-2">
                    You are about to delete: <strong className="text-white">{deleteUserDialog.user?.email}</strong>
                  </p>
                  <p className="text-zinc-400 text-sm">
                    The following will be permanently deleted:
                  </p>
                  <ul className="text-zinc-400 text-sm mt-2 space-y-1 list-disc list-inside">
                    <li>All their songs</li>
                    <li>All their album covers</li>
                    <li>All their gift cards</li>
                    <li>Their account and personal information</li>
                    <li>Any remaining credits</li>
                  </ul>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel 
                className="bg-white/5 border-white/10 text-white hover:bg-white/10"
                disabled={deletingUser}
              >
                Cancel
              </AlertDialogCancel>
              <Button
                onClick={handleDeleteUser}
                disabled={deletingUser}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-user-btn"
              >
                {deletingUser ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete User
                  </>
                )}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </div>
  );
}
